import React, { useState, useEffect, useMemo, useContext, createContext } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    signInAnonymously, 
    onAuthStateChanged, 
    signOut, 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    updateProfile
} from 'firebase/auth'; 
import { 
    getFirestore, 
    collection, 
    addDoc, 
    doc, 
    deleteDoc, 
    updateDoc, 
    query, 
    orderBy, 
    onSnapshot, 
    serverTimestamp, 
    setDoc, 
    getDoc, 
    getDocs, 
    where,
    limit
} from 'firebase/firestore'; 
import { Search, ExternalLink, Menu, X, Shield, Plus, Trash2, Mail, Info, Image as ImageIcon, Upload, ArrowLeft, Layers, Link as LinkIcon, FileUp, Settings, Edit2, CheckCircle, AlertCircle, Send, Globe, ChevronDown, Lock, Wrench, AlertTriangle, Power, Share2, UserCog, RotateCcw, Megaphone, Star, User, LogOut, MessageCircle, Crown, ShieldAlert, ShieldCheck, Heart, Users, Eye, EyeOff, Filter, MoreVertical, ArrowUpDown, ImagePlus, Calendar, UserPlus, MinusCircle, Camera, Undo2, Ban, FileText, AlertOctagon, Copy, Gamepad2, Video } from 'lucide-react'; 

// ==========================================
// --- KONFIGURATION & SETUP ---
// ==========================================

const firebaseConfig = {
  apiKey: "AIzaSyCz74zKDfo-NTxQFN0UK6yqbY8k3JEHE-4",
  authDomain: "bloxstyle-7f28b.firebaseapp.com",
  projectId: "bloxstyle-7f28b",
  storageBucket: "bloxstyle-7f28b.firebasestorage.app",
  messagingSenderId: "748721611162",
  appId: "1:748721611162:web:1983f1c6bf79d84f9d5a23",
  measurementId: "G-6NL4W5SP9T"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = 'bloxstyle-web-v1'; 

const ROBLOX_LINK_PREFIX = "https://www.roblox.com/catalog/";
const ROBLOX_GROUP_LINK = "https://www.roblox.com/de/communities/273092483/BloxStyle-de#!/";
const DISCORD_INVITE_LINK = "https://discord.gg/MZZu38BfKU"; 
const TIKTOK_LINK = "#"; // Coming Soon

// --- DISCORD WEBHOOKS ---
const WEBHOOK_CREATED = "https://discord.com/api/webhooks/1444582288556818543/2F-gEN2tyJmOAuEgwmO7g_lwHTzLMIlO6VGklx8gCeNDJpoO1zK8WoltRhl1tRK9c6x6";
const WEBHOOK_REJECTED = "https://discord.com/api/webhooks/1444582562721824914/7hN_aMXvL03WtoVUBQMT1L_zG4r1o5j0V988_KRrafxNUk7KRwEoG8jynMywQUk1PTJ_";
const WEBHOOK_DELETED = "https://discord.com/api/webhooks/1444582672142700660/rcCRVDwZheXmhyhyoTBMAKgxzqcS8KSSwkG40B1PYJrefCZP9vK3VB3dx5U7PTogAzMt";
const WEBHOOK_COMMENTS = "https://discord.com/api/webhooks/1444582782578720779/lMubyCaPsNZV2LfI984OG4xm0CpparyQLVZ4vPAmf0SB4qLIY6bhJBACmoKudbIxoW9Q";
const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1444069147666419888/i61iY5KPJfl2q1Ypg8Oq1yfEbne8Gtt9Wo9DuLiOWOeiBv5TsLoq8mfrPIDjlAA3u6x3"; // Contact Form

const SUPER_ADMINS = ["admin@roblox-clothes.com"]; 

const isValidRobloxLink = (link) => link && link.startsWith(ROBLOX_LINK_PREFIX);

const getCategories = (outfit) => {
    if (outfit.categories && Array.isArray(outfit.categories)) return outfit.categories;
    if (outfit.category) return [outfit.category];
    return [];
};

// --- CUSTOM ROUTER HOOK ---
const useHashLocation = () => {
  const [loc, setLoc] = useState(window.location.hash.replace('#', '') || '/');
  
  useEffect(() => {
    const handler = () => setLoc(window.location.hash.replace('#', '') || '/');
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const navigate = (to) => {
      window.location.hash = to;
      window.scrollTo(0,0);
  };
  
  return [loc, navigate];
};

// --- DISCORD LOGGER HELPER ---
const sendToDiscord = async (webhookUrl, title, description, color, fields = []) => {
    if (!webhookUrl) return;
    try {
        const embeds = [{
            title: title,
            description: description,
            color: color,
            fields: fields,
            footer: { text: "BloxStyle Logger" },
            timestamp: new Date().toISOString()
        }];
        await fetch(webhookUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ embeds })
        });
    } catch (e) {
        console.error("Discord Webhook Error:", e);
    }
};

// ==========================================
// --- TRANSLATIONS ---
// ==========================================
const translations = {
  de: {
    nav: { home: 'Startseite', gallery: 'Outfits', submit: 'Einreichen', faq: 'FAQ', contact: 'Kontakt', admin: 'Dashboard', login: 'Login', register: 'Registrieren', logout: 'Logout', profile: 'Profil', settings: 'Einstellungen', tos: 'Regeln (ToS)' },
    home: {
      heroTitle: 'Level Up Your', heroTitleHighlight: 'Avatar', heroDesc: 'Die kuratierte Sammlung der besten Roblox Outfits. Finde deinen Style.',
      btnDiscover: 'Outfits entdecken', trendingTitle: 'Trending Outfits', viewAll: 'Alle anzeigen', noTrending: 'Noch keine Trending Outfits.'
    },
    gallery: { 
        searchPlaceholder: 'Suche nach Outfits, Styles...', filterAll: 'Alle', noOutfitsTitle: 'Nichts gefunden', noOutfitsDesc: 'Versuche es mit anderen Suchbegriffen.',
        sortNew: 'Neueste', sortOld: 'Älteste', sortPriceHigh: 'Preis (Hoch)', sortPriceLow: 'Preis (Niedrig)', sortAZ: 'A-Z'
    },
    detail: {
      back: 'Zurück zur Übersicht', contains: 'Enthaltene Items', totalPrice: 'Gesamtpreis', buy: 'Bei Roblox kaufen', comments: 'Community Feedback', rate: 'Bewerten',
      loginToComment: 'Bitte einloggen, um mitzureden.', commentPlaceholder: 'Teile deine Meinung...', sendComment: 'Kommentieren',
      noteTitle: 'Wichtiger Hinweis', noteDesc: 'Wir verkaufen diese Items nicht selbst. Der Link leitet dich direkt zum offiziellen Roblox Marktplatz weiter. Preise können variieren.',
      rated: 'Du hast bereits abgestimmt.', ratingThanks: 'Danke für deine Bewertung!',
      deleteComment: 'Kommentar löschen?', uploadedBy: 'Hochgeladen von'
    },
    auth: { 
        loginTitle: 'Willkommen zurück', registerTitle: 'Account erstellen', name: 'Dein Nutzername', email: 'Email Adresse', password: 'Passwort', 
        btnLogin: 'Einloggen', btnRegister: 'Kostenlos registrieren', switchReg: 'Neu hier? Jetzt registrieren', switchLog: 'Hast du schon einen Account? Login',
        successReg: 'Willkommen bei BloxStyle!', errorGeneric: 'Ein Fehler ist aufgetreten.', loginReq: 'Bitte logge dich ein, um Outfits hochzuladen.'
    },
    profile: { 
        title: 'Profil', role: 'Rolle', joined: 'Mitglied seit', edit: 'Profil bearbeiten', save: 'Speichern', uploads: 'Beiträge', uploadImg: 'Bild hochladen', emailHidden: '', 
        bioPlaceholder: 'Schreibe etwas über dich...', noBio: 'Keine Beschreibung verfügbar.', settingsTitle: 'Profil Einstellungen', noUploads: 'Noch keine Outfits hochgeladen.', 
        robloxLink: 'Roblox Profil Link', robloxBtn: 'Roblox Profil öffnen', robloxPlaceholder: 'https://www.roblox.com/users/...',
        usernameTaken: 'Nutzername bereits vergeben.', usernameInvalid: 'Ungültige Zeichen im Namen.', cooldown: 'Du kannst deinen Namen erst wieder ändern am: ',
        copyId: 'ID Kopieren', idCopied: 'User ID kopiert!'
    },
    admin: {
      title: 'Admin Dashboard', accessDenied: 'Zugriff verweigert. Nur für das Team.', maintenanceOn: 'WARTUNGSMODUS AKTIV', maintenanceOff: 'SYSTEM ONLINE',
      tabManage: 'Outfits verwalten', tabReviews: 'Warteschlange', tabRejected: 'Papierkorb', tabRoles: 'User Verwaltung',
      roleUser: 'User', roleVip: 'VIP', roleMod: 'Moderator', roleAdmin: 'Administrator',
      assignRole: 'Rolle zuweisen', emailPlaceholder: 'Email des Users', searchUser: 'User suchen...', sortName: 'Name', sortRole: 'Rolle', sortDate: 'Datum',
      announceTitle: 'Globale Ankündigung', announcePlaceholderTitle: 'Titel (z.B. Update)', announcePlaceholderMsg: 'Nachricht an alle Besucher...', announceSet: 'Veröffentlichen', announceDel: 'Entfernen',
      editOutfit: 'Outfit bearbeiten', saveChanges: 'Änderungen speichern', discard: 'Verwerfen',
      itemsEdit: 'Items bearbeiten', itemAdd: 'Item hinzufügen',
      thUser: 'User', thRole: 'Rolle', thDate: 'Datum', thActions: 'Aktionen',
      thImg: 'Bild', thTitle: 'Titel', thPrice: 'Preis', thUploader: 'Uploader', thStatus: 'Status',
      statusActive: 'Aktiv', statusInactive: 'Inaktiv',
      banUser: 'Bannen', unbanUser: 'Entbannen', confirmBan: 'Diesen Nutzer wirklich bannen?'
    },
    submit: {
        title: 'Outfit einreichen', desc: 'Teile deinen Style mit der Community.', successTitle: 'Vielen Dank!', successDesc: 'Dein Outfit wird nun von einem Moderator geprüft.', btnMore: 'Weiteres Outfit einreichen',
        formTitle: 'Name des Outfits', formCat: 'Kategorien wählen', formDesc: 'Beschreibung (Optional)', imgLabel: 'Vorschau Bild', link: 'Bild Link', upload: 'Datei Upload', itemLabel: 'Outfit Bestandteile', btnSubmit: 'Einreichen',
        addItem: 'Item hinzufügen', itemName: 'Item Name', itemLink: 'Roblox Link', itemPrice: 'Preis',
        reqTitle: 'Titel fehlt', reqImg: 'Bild fehlt', reqCat: 'Mindestens eine Kategorie', reqItems: 'Alle Item-Felder sind Pflicht!', reqLink: 'Link muss mit https://www.roblox.com/catalog/ beginnen'
    },
    contact: { title: 'Kontakt', desc: 'Fragen oder Feedback?', name: 'Dein Name', email: 'Deine Email', msg: 'Nachricht', send: 'Nachricht senden', success: 'Nachricht gesendet!' },
    maintenance: { title: 'Wartungsarbeiten', desc: 'Wir verbessern die Seite gerade für dich. Wir sind gleich zurück!', adminLogin: 'Team Login' },
    footer: '© 2025 BloxStyle Showcase. Not affiliated with Roblox Corporation.',
    discord: 'Discord Server beitreten',
    robloxGroup: 'Roblox Gruppe',
    tiktok: 'TikTok (Bald)',
    faq: {
        title: 'Häufig gestellte Fragen',
        items: [
            { q: "Wie kaufe ich die Outfits?", a: "Klicke auf 'Kaufen' beim Item. Du wirst zu Roblox weitergeleitet." },
            { q: "Kann ich eigene Outfits hochladen?", a: "Ja, erstelle einen Account und nutze 'Einreichen'." },
            { q: "Wie werde ich VIP oder Mod?", a: "Sei aktiv und hilfreich. Infos dazu auf unserem Discord." },
            { q: "Ist BloxStyle kostenlos?", a: "Ja, 100% kostenlos." },
            { q: "Warum wurde mein Outfit abgelehnt?", a: "Meist wegen schlechter Bildqualität oder falschen Links." },
            { q: "Fehler gefunden?", a: "Melde dich über Kontakt oder Discord." }
        ]
    },
    tos: {
        title: 'Nutzungsbedingungen (Terms of Service)',
        content: `Willkommen bei BloxStyle. Durch die Nutzung unserer Dienste stimmst du folgenden Regeln zu:

1. Allgemeine Regeln
Behandle andere Nutzer mit Respekt. Beleidigungen, Hassrede oder Belästigung werden nicht toleriert und führen zum sofortigen Ausschluss.

2. Inhalte & Copyright
Du darfst nur Inhalte hochladen, an denen du die Rechte besitzt oder die frei verfügbar sind. Das Hochladen von unangemessenen, pornografischen oder gewaltverherrlichenden Inhalten ist strengstens untersagt.

3. Roblox Links
Wir sind eine Fan-Seite. Alle Links zu Roblox-Items führen zum offiziellen Marktplatz. Wir sind nicht verantwortlich für Käufe, die auf der Roblox-Plattform getätigt werden.

4. Account-Sicherheit
Du bist für die Sicherheit deines Accounts verantwortlich. Teile dein Passwort niemals mit anderen. BloxStyle-Admins werden dich niemals nach deinem Passwort fragen.

5. Maßnahmen bei Verstößen
Wir behalten uns das Recht vor, Nutzerkonten bei Verstößen gegen diese Regeln temporär oder dauerhaft zu sperren (Bann).

Stand: November 2025`
    },
    ui: { 
        confirmTitle: 'Bestätigung erforderlich', confirmBtn: 'Ja, Ausführen', cancelBtn: 'Abbrechen', loading: 'Lade Daten...', maxSize: 'Maximal 500KB erlaubt.', 
        confirmDelete: 'Möchtest du dieses Element wirklich endgültig löschen?', confirmTrash: 'In den Papierkorb verschieben?', confirmReject: 'Soll dieses Outfit abgelehnt werden?', 
        selectFile: 'Datei auswählen', confirmRestore: 'Wiederherstellen?', clickToUpload: 'Klicken zum Hochladen', bannedTitle: 'Account gesperrt', bannedDesc: 'Dein Account wurde aufgrund eines Verstoßes gegen unsere Nutzungsbedingungen gesperrt.' 
    },
    card: { items: 'Items', view: 'Ansehen' }
  },
  en: {
    nav: { home: 'Home', gallery: 'Outfits', submit: 'Submit', faq: 'FAQ', contact: 'Contact', admin: 'Dashboard', login: 'Login', register: 'Register', logout: 'Logout', profile: 'Profile', settings: 'Settings', tos: 'Terms (ToS)' },
    home: {
      heroTitle: 'Level Up Your', heroTitleHighlight: 'Avatar', heroDesc: 'Curated Roblox outfits collection. Find your style.',
      btnDiscover: 'Discover Outfits', trendingTitle: 'Trending', viewAll: 'View All', noTrending: 'No trending outfits yet.'
    },
    gallery: { 
        searchPlaceholder: 'Search outfits...', filterAll: 'All', noOutfitsTitle: 'Nothing found', noOutfitsDesc: 'Try different keywords.',
        sortNew: 'Newest', sortOld: 'Oldest', sortPriceHigh: 'Price (High)', sortPriceLow: 'Price (Low)', sortAZ: 'A-Z'
    },
    detail: {
      back: 'Back', contains: 'Included Items', totalPrice: 'Total Price', buy: 'Buy on Roblox', comments: 'Community Feedback', rate: 'Rate this',
      loginToComment: 'Please login to join the discussion.', commentPlaceholder: 'Share your thoughts...', sendComment: 'Post Comment',
      noteTitle: 'Disclaimer', noteDesc: 'We do not sell these items directly. Links redirect to the official Roblox marketplace.',
      rated: 'You already voted.', ratingThanks: 'Thanks for rating!',
      deleteComment: 'Delete comment?', uploadedBy: 'Uploaded by'
    },
    auth: { 
        loginTitle: 'Welcome Back', registerTitle: 'Create Account', name: 'Username', email: 'Email', password: 'Password', 
        btnLogin: 'Login', btnRegister: 'Register', switchReg: 'New here? Register', switchLog: 'Have an account? Login',
        successReg: 'Welcome to BloxStyle!', errorGeneric: 'An error occurred.', loginReq: 'Please login to upload outfits.'
    },
    profile: { 
        title: 'Profile', role: 'Role', joined: 'Joined', edit: 'Edit Profile', save: 'Save Changes', uploads: 'Uploads', uploadImg: 'Change Image', emailHidden: '', 
        bioPlaceholder: 'Write something about yourself...', noBio: 'No bio available.', settingsTitle: 'Profile Settings', noUploads: 'No outfits uploaded yet.', 
        robloxLink: 'Roblox Profile Link', robloxBtn: 'Open Roblox Profile', robloxPlaceholder: 'https://www.roblox.com/users/...',
        usernameTaken: 'Username already taken.', usernameInvalid: 'Invalid characters in username.', cooldown: 'You can change your name again on: ',
        copyId: 'Copy ID', idCopied: 'User ID copied!'
    },
    admin: {
      title: 'Admin Dashboard', accessDenied: 'Access denied.', maintenanceOn: 'MAINTENANCE MODE', maintenanceOff: 'SYSTEM ONLINE',
      tabManage: 'Manage Outfits', tabReviews: 'Reviews', tabRejected: 'Rejected', tabRoles: 'User Management',
      roleUser: 'User', roleVip: 'VIP', roleMod: 'Moderator', roleAdmin: 'Administrator',
      assignRole: 'Assign Role', emailPlaceholder: 'User Email', searchUser: 'Search users...', sortName: 'Name', sortRole: 'Role', sortDate: 'Date',
      announceTitle: 'Global Announcement', announcePlaceholderTitle: 'Title...', announcePlaceholderMsg: 'Message...', announceSet: 'Publish', announceDel: 'Remove',
      editOutfit: 'Edit Outfit', saveChanges: 'Save Changes', discard: 'Discard',
      itemsEdit: 'Edit Items', itemAdd: 'Add Item',
      thUser: 'User', thRole: 'Role', thDate: 'Date', thActions: 'Actions',
      thImg: 'Img', thTitle: 'Title', thPrice: 'Price', thUploader: 'Uploader', thStatus: 'Status',
      statusActive: 'Active', statusInactive: 'Inactive',
      banUser: 'Ban User', unbanUser: 'Unban User', confirmBan: 'Ban this user?', 
      formImgNote: 'Image URL (or leave empty)', catManage: 'Categories', modManage: 'Moderators', 
      newCatPlaceholder: 'New Category', newModPlaceholder: 'Mod Email', add: 'Add',
      noPending: 'No pending submissions.', noRejected: 'No rejected outfits.', pendingTag: 'Pending', rejectedTag: 'Rejected',
      edit: 'Edit', approve: 'Approve', reject: 'Reject', restore: 'Restore', cancel: 'Cancel', new: 'New',
      saveErr: 'Error saving', imgMissing: 'Image Missing'
    },
    submit: {
        title: 'Submit Outfit', desc: 'Share your style with the world.', successTitle: 'Thanks!', successDesc: 'Your outfit is under review.', btnMore: 'Submit another',
        formTitle: 'Outfit Title', formCat: 'Categories', formDesc: 'Description', imgLabel: 'Image', link: 'Link', upload: 'Upload', itemLabel: 'Items', btnSubmit: 'Submit',
        addItem: 'Add Item', itemName: 'Name', itemLink: 'Link', itemPrice: 'Price',
        reqTitle: 'Title missing', reqImg: 'Image missing', reqCat: 'Select category', reqItems: 'All item fields required', reqLink: 'Link must start with https://www.roblox.com/catalog/',
        uploadSelected: 'Image Selected', uploadPlaceholder: 'Choose File', sending: 'Sending...'
    },
    contact: { title: 'Contact', desc: 'Questions?', name: 'Name', email: 'Email', msg: 'Message', send: 'Send Message', success: 'Message sent!', loaded: 'Image loaded', btnNew: 'New Message' },
    maintenance: { title: 'Maintenance', desc: 'Be back soon!', adminLogin: 'Admin Login' },
    footer: '© 2025 BloxStyle Showcase. Not affiliated with Roblox Corporation.',
    discord: 'Discord Server',
    robloxGroup: 'Roblox Group',
    tiktok: 'TikTok (Soon)',
    faq: {
        title: 'FAQ',
        items: [
            { q: "How to buy?", a: "Click 'Buy' on the item. You will be redirected to Roblox." },
            { q: "Can I submit?", a: "Yes, create an account and use 'Submit'." },
            { q: "How to get VIP?", a: "Be active and helpful. Join our Discord." },
             { q: "Is it free?", a: "Yes, 100% free." },
             { q: "Why rejected?", a: "Probably bad quality or broken links." },
             { q: "Found a bug?", a: "Report via contact form or Discord." }
        ]
    },
    tos: {
        title: 'Terms of Service',
        content: `Welcome to BloxStyle. By using our service you agree to:

1. General Rules
Be respectful. Harassment, hate speech, or spam will result in an immediate ban.

2. Content & Copyright
Only upload content you have rights to or is public. Inappropriate content is strictly prohibited.

3. Roblox Links
We are a fan site. All links redirect to the official Roblox marketplace. We are not responsible for purchases made on Roblox.

4. Account Security
You are responsible for your account. Never share your password. Admins will never ask for it.

5. Violations
We reserve the right to suspend or ban accounts that violate these terms.

Last updated: November 2025`
    },
    ui: { 
        confirmTitle: 'Are you sure?', confirmBtn: 'Yes', cancelBtn: 'Cancel', loading: 'Loading...', maxSize: 'Max 500KB', 
        confirmDelete: 'Permanently delete this item?', confirmTrash: 'Move to trash?', confirmReject: 'Reject this outfit?', 
        selectFile: 'Select File', confirmRestore: 'Restore Outfit?', clickToUpload: 'Click to upload image', bannedTitle: 'Account Suspended', bannedDesc: 'Your account has been banned for violating our Terms of Service.' 
    },
    card: { items: 'Items', view: 'View' }
  }
};

// ==========================================
// --- CONTEXTS & PROVIDERS ---
// ==========================================

const LanguageContext = createContext();
const UIContext = createContext();

const UIProvider = ({ children, t }) => {
    const [toast, setToast] = useState(null); 
    const [modal, setModal] = useState(null); 

    const showToast = (msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };
    const confirm = (msg, onConfirm) => setModal({ msg, onConfirm });

    return (
        <UIContext.Provider value={{ showToast, confirm }}>
            {children}
            {/* Toast Notification */}
            {toast && (
                <div className={`fixed bottom-4 left-4 px-6 py-4 rounded-lg shadow-xl text-white z-[90] animate-fade-in flex items-center gap-3 ${toast.type === 'error' ? 'bg-red-600' : 'bg-indigo-600'}`}>
                    {toast.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />} <span className="font-medium">{toast.msg}</span>
                </div>
            )}
            {/* Confirmation Modal */}
            {modal && (
                <div className="fixed inset-0 bg-slate-950/80 flex items-center justify-center z-[100] backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-2xl max-w-sm w-full transform scale-100 transition-all">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><AlertTriangle className="text-indigo-500" /> {t.ui.confirmTitle}</h3>
                        <p className="text-slate-300 mb-8">{modal.msg}</p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setModal(null)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors">{t.ui.cancelBtn}</button>
                            <button onClick={() => { modal.onConfirm(); setModal(null); }} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold transition-colors">{t.ui.confirmBtn}</button>
                        </div>
                    </div>
                </div>
            )}
        </UIContext.Provider>
    );
};

// ==========================================
// --- COMPONENTS: UI ELEMENTS ---
// ==========================================

const BloxLogo = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 2L2.5 8.75L16 15.5L29.5 8.75L16 2Z" fill="#818cf8" />
    <path d="M2.5 8.75V23.25L16 30V15.5L2.5 8.75Z" fill="#6366f1" />
    <path d="M29.5 8.75V23.25L16 30V15.5L29.5 8.75Z" fill="#4f46e5" />
  </svg>
);

const RoleBadge = ({ role }) => {
    let color = 'bg-slate-700 text-slate-300 border-slate-600';
    let Icon = null;

    if (role === 'admin') { color = 'bg-red-500/10 text-red-400 border-red-500/30'; Icon = ShieldAlert; }
    else if (role === 'mod') { color = 'bg-blue-500/10 text-blue-400 border-blue-500/30'; Icon = ShieldCheck; }
    else if (role === 'vip') { color = 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'; Icon = Crown; }

    if (!Icon) return null; 

    return (
        <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold border ${color}`}>
            <Icon size={10} /> {role}
        </span>
    );
};

const BannedScreen = ({ onLogout }) => {
    const { t } = useContext(LanguageContext);
    return (
        <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center p-4 text-center">
            <div className="bg-slate-900 p-12 rounded-3xl shadow-2xl border border-red-900/50 max-w-lg w-full relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-orange-500"></div>
                <Ban className="w-24 h-24 text-red-500 mx-auto mb-6 opacity-80" />
                <h1 className="text-3xl font-bold text-white mb-4">{t.ui.bannedTitle}</h1>
                <p className="text-slate-400 mb-8 leading-relaxed">{t.ui.bannedDesc}</p>
                <div className="flex justify-center">
                    <button onClick={onLogout} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-bold transition-colors">
                        {t.nav.logout}
                    </button>
                </div>
            </div>
        </div>
    );
};

const MaintenanceScreen = ({ navigate }) => {
    const { t } = useContext(LanguageContext);
    return (
        <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center p-4 text-center">
            <div className="bg-slate-900 p-12 rounded-3xl shadow-2xl border border-slate-800 max-w-lg w-full relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                <Wrench className="w-24 h-24 text-indigo-500 mx-auto mb-6 opacity-80" />
                <h1 className="text-3xl font-bold text-white mb-4">{t.maintenance.title}</h1>
                <p className="text-slate-400 mb-8 leading-relaxed">{t.maintenance.desc}</p>
                <div className="flex justify-center">
                    <button onClick={() => navigate('/login')} className="text-xs text-slate-600 hover:text-indigo-400 flex items-center gap-2 transition-colors">
                        <Lock size={12} /> {t.maintenance.adminLogin}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AnnouncementPopup = ({ message, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);
    
    const handleClose = () => {
        setIsExiting(true);
        setTimeout(onClose, 500); 
    };

    if (!message) return null;
    
    const title = message.title || 'NEWS';
    const text = message.message || message;

    return (
        <div 
            className={`fixed bottom-4 right-4 bg-indigo-600 text-white p-4 rounded-lg shadow-2xl border border-indigo-500 max-w-sm z-[80] flex items-start gap-3 pr-8 transition-all duration-500 ease-in-out transform 
            ${isExiting ? 'opacity-0 translate-y-10 scale-95' : 'opacity-100 translate-y-0 scale-100'} animate-fade-in`}
        >
            <Megaphone className="flex-shrink-0 mt-1" size={20} />
            <div>
                <h4 className="font-bold text-sm uppercase tracking-wide mb-1">{title}</h4>
                <p className="text-sm text-indigo-100">{text}</p>
            </div>
            <button onClick={handleClose} className="absolute top-2 right-2 text-indigo-200 hover:text-white transition-colors"><X size={16} /></button>
        </div>
    );
};

// ==========================================
// --- COMPONENTS: MAIN STRUCTURE ---
// ==========================================

const Navbar = ({ navigate, view, user, onLogout, userProfile, goToProfile, goToSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { t, lang, changeLanguage } = useContext(LanguageContext);
  
  const isSuperAdmin = user && SUPER_ADMINS.includes(user.email);
  const role = isSuperAdmin ? 'admin' : (userProfile?.role || 'user');
  
  const isAdmin = role === 'admin';
  const isMod = role === 'mod';
  const isVip = role === 'vip';

  const navItems = [ 
      { id: 'home', label: t.nav.home, path: '/' }, 
      { id: 'gallery', label: t.nav.gallery, path: '/gallery' }, 
      { id: 'submit', label: t.nav.submit, path: '/submit' }, 
      { id: 'faq', label: t.nav.faq, path: '/faq' },
      { id: 'contact', label: t.nav.contact, path: '/contact' }
  ];

  return (
    <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50 shadow-lg shadow-slate-950/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center cursor-pointer group" onClick={() => navigate('/')}>
            <BloxLogo className="h-10 w-10 text-indigo-500 group-hover:scale-110 transition-transform duration-300" />
            <span className="ml-3 text-xl font-bold text-white tracking-wider">BLOX<span className="text-indigo-500">STYLE</span></span>
          </div>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-4">
              {navItems.map((item) => (
                <button key={item.id} onClick={() => navigate(item.path)} className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${view === item.id ? 'bg-slate-800 text-indigo-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`}>
                    {item.label}
                </button>
              ))}
              
              {(isAdmin || isMod) && (
                  <button onClick={() => navigate('/admin')} className={`px-3 py-2 rounded-md text-sm font-bold border ${view === 'admin' ? 'bg-indigo-600 text-white border-indigo-500' : 'text-indigo-400 border-indigo-500/30 hover:bg-indigo-900/50'}`}>
                    {t.nav.admin}
                  </button>
              )}
              
              <div className="h-6 w-px bg-slate-700 mx-2"></div>
              
              {/* Language Dropdown */}
              <div className="relative">
                  <button onClick={() => setIsLangOpen(!isLangOpen)} className="text-slate-400 hover:text-white flex items-center gap-1 text-xs font-bold border border-slate-700 px-2 py-1 rounded uppercase transition-colors">
                      <Globe size={12}/> {lang} <ChevronDown size={10} />
                  </button>
                  {isLangOpen && (
                      <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-700 rounded shadow-xl overflow-hidden min-w-[120px] z-50">
                          <button onClick={() => { changeLanguage('de'); setIsLangOpen(false); }} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-700 ${lang === 'de' ? 'text-indigo-400 font-bold' : 'text-slate-300'}`}>Deutsch</button>
                          <button onClick={() => { changeLanguage('en'); setIsLangOpen(false); }} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-700 ${lang === 'en' ? 'text-indigo-400 font-bold' : 'text-slate-300'}`}>English</button>
                      </div>
                  )}
              </div>

              {user && !user.isAnonymous ? (
                  <div className="relative ml-4">
                      <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-2 group pl-2 border-l border-slate-700">
                          <div className="w-8 h-8 rounded-full bg-slate-700 overflow-hidden border border-slate-600 group-hover:border-indigo-500 transition-colors relative">
                              {userProfile?.photoURL ? <img src={userProfile.photoURL} className="w-full h-full object-cover" /> : <User className="w-full h-full p-1.5 text-slate-400" />}
                          </div>
                          <ChevronDown size={12} className="text-slate-500"/>
                      </button>
                      
                      {isUserMenuOpen && (
                          <div className="absolute top-full right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50">
                              <div className="px-4 py-3 border-b border-slate-700">
                                  <p className="text-sm font-bold text-white truncate">{userProfile?.username || user.displayName || 'User'}</p>
                                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                              </div>
                              <button onClick={() => { navigate(`/profile/${userProfile?.username || user.uid}`); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2"><User size={14}/> {t.nav.profile}</button>
                              <button onClick={() => { goToSettings(); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2"><Settings size={14}/> {t.nav.settings}</button>
                              <div className="border-t border-slate-700"></div>
                              <button onClick={() => { onLogout(); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700 hover:text-red-300 flex items-center gap-2"><LogOut size={14}/> {t.nav.logout}</button>
                          </div>
                      )}
                  </div>
              ) : (
                  <button onClick={() => navigate('/login')} className="ml-4 text-sm font-bold text-white bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-full border border-slate-700 transition-all shadow-md">
                    {t.nav.login}
                  </button>
              )}
          </div>

          {/* Mobile Menu Button */}
          <div className="-mr-2 flex md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="bg-slate-800 p-2 rounded-md text-slate-400 hover:text-white transition-colors">
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 shadow-xl animate-fade-in p-4 space-y-2">
             {navItems.map((item) => (
                 <button key={item.id} onClick={() => { navigate(item.path); setIsOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:bg-slate-800 hover:text-white">
                     {item.label}
                 </button>
             ))}
              {(isAdmin || isMod) && (
                  <button onClick={() => { navigate('/admin'); setIsOpen(false); }} className="block w-full text-left px-3 py-2 text-indigo-400 font-bold bg-indigo-900/20 rounded">
                      {t.nav.admin}
                  </button>
              )}
              <div className="border-t border-slate-800 pt-3 mt-2">
                  {user && !user.isAnonymous ? (
                     <>
                        <button onClick={() => { navigate(`/profile/${userProfile?.username || user.uid}`); setIsOpen(false); }} className="block w-full text-left px-3 py-2 text-slate-300 hover:text-white">{t.nav.profile}</button>
                        <button onClick={() => { goToSettings(); setIsOpen(false); }} className="block w-full text-left px-3 py-2 text-slate-300 hover:text-white">{t.nav.settings}</button>
                        <button onClick={() => { onLogout(); setIsOpen(false); }} className="block w-full text-left px-3 py-2 text-red-400 hover:bg-red-500/10 rounded">{t.nav.logout}</button>
                     </>
                  ) : (
                     <button onClick={() => { navigate('/login'); setIsOpen(false); }} className="block w-full text-left px-3 py-2 font-bold text-white bg-indigo-600 rounded">
                        {t.nav.login}
                     </button>
                  )}
              </div>
        </div>
      )}
    </nav>
  );
};

// ==========================================
// --- COMPONENTS: PAGES ---
// ==========================================

const Home = ({ navigate, featuredOutfits, onSelectOutfit }) => {
  const { t } = useContext(LanguageContext);
  return (
  <div className="space-y-12 pb-12 animate-fade-in">
    <div className="relative bg-slate-900 overflow-hidden border-b border-slate-800">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/20 to-purple-900/20"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 text-center">
        <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-4 drop-shadow-lg">{t.home.heroTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">{t.home.heroTitleHighlight}</span></h1>
        <p className="mt-4 max-w-2xl mx-auto text-xl text-slate-400">{t.home.heroDesc}</p>
        <div className="mt-8 flex justify-center gap-4">
          <button onClick={() => navigate('/gallery')} className="bg-indigo-600 text-white px-8 py-3 rounded-full font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/25 hover:scale-105">{t.home.btnDiscover}</button>
        </div>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-end mb-8">
        <div><h2 className="text-2xl font-bold text-white">{t.home.trendingTitle}</h2></div>
        <button onClick={() => navigate('/gallery')} className="text-indigo-400 hover:text-indigo-300 text-sm font-medium flex items-center gap-1 hover:underline">{t.home.viewAll} <ExternalLink className="w-4 h-4" /></button>
      </div>
      {featuredOutfits.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{featuredOutfits.slice(0, 4).map(outfit => (<OutfitCard key={outfit.id} outfit={outfit} onClick={() => navigate('/outfit/' + outfit.id)} />))}</div>
      ) : (<div className="text-center py-12 bg-slate-800/50 rounded-xl border border-dashed border-slate-700"><p className="text-slate-400">{t.home.noTrending}</p></div>)}
    </div>
  </div>
)};

const Gallery = ({ outfits, navigate, categories }) => {
  const { t } = useContext(LanguageContext);
  const [filter, setFilter] = useState('Alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // Newest, Oldest, PriceLow, PriceHigh, AZ

  const filteredOutfits = useMemo(() => {
    let results = outfits.filter(outfit => {
      const outfitCats = getCategories(outfit);
      const matchesCategory = filter === 'Alle' || outfitCats.includes(filter);
      const matchesSearch = outfit.title.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    // Sorting Logic
    results.sort((a, b) => {
        if (sortOrder === 'newest') return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        if (sortOrder === 'oldest') return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
        
        const priceA = a.items?.reduce((sum, item) => sum + (Number(item.price) || 0), 0) || 0;
        const priceB = b.items?.reduce((sum, item) => sum + (Number(item.price) || 0), 0) || 0;
        
        if (sortOrder === 'priceHigh') return priceB - priceA;
        if (sortOrder === 'priceLow') return priceA - priceB;
        if (sortOrder === 'az') return a.title.localeCompare(b.title);
        return 0;
    });

    return results;
  }, [outfits, filter, searchTerm, sortOrder]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 min-h-screen animate-fade-in">
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
          <input type="text" placeholder={t.gallery.searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white pl-10 pr-4 py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all shadow-md" />
        </div>
        <div className="relative min-w-[160px]">
             <select 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-lg focus:border-indigo-500 outline-none appearance-none cursor-pointer"
             >
                 <option value="newest">{t.gallery.sortNew}</option>
                 <option value="oldest">{t.gallery.sortOld}</option>
                 <option value="priceHigh">{t.gallery.sortPriceHigh}</option>
                 <option value="priceLow">{t.gallery.sortPriceLow}</option>
                 <option value="az">{t.gallery.sortAZ}</option>
             </select>
             <ArrowUpDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
        </div>
      </div>
      
      {/* Category Filter */}
      <div className="flex overflow-x-auto pb-4 gap-2 custom-scrollbar mb-8">
          <button onClick={() => setFilter('Alle')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all shadow-md ${filter === 'Alle' ? 'bg-indigo-600 text-white shadow-indigo-500/25' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>{t.gallery.filterAll}</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all shadow-md ${filter === cat ? 'bg-indigo-600 text-white shadow-indigo-500/25' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>{cat}</button>
          ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredOutfits.map(outfit => (<OutfitCard key={outfit.id} outfit={outfit} onClick={() => navigate('/outfit/' + outfit.id)} />))}
      </div>
      {filteredOutfits.length === 0 && (
          <div className="text-center py-20 text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50"/>
              <h3 className="text-lg text-white font-bold">{t.gallery.noOutfitsTitle}</h3>
              <p>{t.gallery.noOutfitsDesc}</p>
          </div>
      )}
    </div>
  );
};

const OutfitCard = ({ outfit, onClick }) => {
  const { t } = useContext(LanguageContext);
  const totalPrice = outfit.items ? outfit.items.reduce((sum, item) => sum + (Number(item.price) || 0), 0) : outfit.price;
  const categories = getCategories(outfit);

  return (
    <div onClick={onClick} className="bg-slate-800 rounded-xl overflow-hidden shadow-lg border border-slate-700 hover:border-indigo-500 hover:shadow-indigo-500/10 transition-all duration-300 group cursor-pointer h-full flex flex-col relative">
      <div className="relative aspect-[3/4] overflow-hidden bg-slate-900">
        <img 
          src={outfit.image} 
          alt={outfit.title} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = "https://placehold.co/400x500/1e293b/FFF?text=Roblox+Outfit"; }}
        />
        <div className="absolute top-2 right-2 bg-slate-900/80 backdrop-blur-sm px-2 py-1 rounded text-xs font-bold text-white border border-slate-600">
          {totalPrice} R$
        </div>
        {outfit.status === 'inactive' && (
            <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2 py-1 rounded font-bold uppercase">
                Inactive
            </div>
        )}
        {outfit.ratingAvg > 0 && (
             <div className="absolute bottom-2 left-2 bg-slate-900/80 backdrop-blur-sm px-2 py-1 rounded text-xs font-bold text-yellow-400 border border-slate-600 flex items-center gap-1">
                 <Star size={10} fill="currentColor" /> {outfit.ratingAvg.toFixed(1)}
             </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-bold text-white truncate pr-2 group-hover:text-indigo-400 transition-colors">{outfit.title}</h3>
        </div>
        <div className="flex flex-wrap gap-1 mb-2">
            {categories.slice(0, 3).map((cat, idx) => (
                <span key={idx} className="text-[10px] bg-indigo-900/50 text-indigo-200 border border-indigo-500/20 px-2 py-0.5 rounded-full">{cat}</span>
            ))}
            {categories.length > 3 && <span className="text-[10px] text-slate-500 px-1">+{categories.length - 3}</span>}
        </div>
        <p className="text-slate-400 text-sm line-clamp-2 mb-auto">{outfit.description}</p>
      </div>
    </div>
  );
};

// --- OUTFIT DETAIL & COMMENTS ---
const CommentsSection = ({ outfitId, user, t, showToast, navigate, confirm, currentUserRole }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Fetch comments
    useEffect(() => {
        if(!outfitId) return;
        const q = query(
            collection(db, 'artifacts', appId, 'public', 'data', 'comments'), 
            where('outfitId', '==', outfitId), 
            limit(50)
        );
        const unsub = onSnapshot(q, (snap) => {
            const rawComments = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const sortedComments = rawComments.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setComments(sortedComments);
        });
        return () => unsub();
    }, [outfitId]);

    const handleSend = async () => {
        if (!newComment.trim()) return;
        setSubmitting(true);
        try {
            let role = 'user';
            let photo = '';
            let username = 'User';
            
            if (!user.isAnonymous) {
                 // Fetch fresh profile data for comment
                 const p = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', user.uid));
                 if(p.exists()) {
                     const d = p.data();
                     role = d.role || 'user';
                     photo = d.photoURL;
                     username = d.username;
                 }
                 if(SUPER_ADMINS.includes(user.email)) role = 'admin';
            }

            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'comments'), {
                outfitId,
                text: newComment,
                userId: user.uid,
                userName: username,
                userPhoto: photo, 
                userRole: role,
                createdAt: serverTimestamp()
            });
            
            // DISCORD LOG
            sendToDiscord(WEBHOOK_COMMENTS, "New Comment Posted", `User: ${username}\nContent: ${newComment}`, 3447003, [
                 { name: "Outfit ID", value: outfitId, inline: true }
            ]);
            
            setNewComment('');
        } catch (err) { console.error(err); showToast('Error posting comment', 'error'); }
        setSubmitting(false);
    };

    const handleDelete = async (commentId, commentContent) => {
        confirm(t.detail.deleteComment, async () => {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'comments', commentId));
            
            // DISCORD LOG
            sendToDiscord(WEBHOOK_COMMENTS, "Comment Deleted", `Content was: ${commentContent || 'Unknown'}`, 15548997, [
                 { name: "Comment ID", value: commentId, inline: true }
            ]);
            
            showToast('Deleted', 'success');
        });
    };

    return (
        <div className="mt-8 pt-8 border-t border-slate-700">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2"><MessageCircle size={20} className="text-indigo-400" /> {t.detail.comments} <span className="bg-slate-700 text-slate-300 text-xs px-2 py-0.5 rounded-full">{comments.length}</span></h3>
            
            {user && !user.isAnonymous ? (
                <div className="flex gap-4 mb-8 bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex-shrink-0 overflow-hidden">
                       {/* Display current user's photo in input area. We need to fetch it or pass it down. For simplicity assuming parent passes or we fetch. But here we rely on parent 'user' object which might not have the latest Firestore photo. The fix in handleSend ensures the comment HAS the right photo, but display here might lag. */}
                        <User className="p-2 w-full h-full text-slate-400"/>
                    </div>
                    <div className="flex-grow relative">
                        <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder={t.detail.commentPlaceholder} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white focus:border-indigo-500 outline-none min-h-[80px]" />
                        <button onClick={handleSend} disabled={submitting || !newComment.trim()} className="mt-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center justify-center gap-2 shadow-lg w-full md:w-auto ml-auto">
                            {t.detail.sendComment} <Send size={14}/>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="bg-slate-800/50 p-6 rounded-xl text-center text-slate-400 mb-8 border border-slate-700 border-dashed flex flex-col items-center gap-2">
                    <Lock size={24} className="text-slate-500 mb-2"/>
                    {t.detail.loginToComment}
                </div>
            )}

            <div className="space-y-4">
                {comments.map(c => {
                    const canDelete = currentUserRole === 'admin' || currentUserRole === 'mod' || (user && user.uid === c.userId);
                    return (
                        <div key={c.id} className="flex gap-4 group bg-slate-800/30 p-4 rounded-xl border border-slate-700/50 hover:bg-slate-800 transition-colors relative">
                            <div onClick={() => navigate(`/profile/${c.userName || c.userId}`)} className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex-shrink-0 overflow-hidden cursor-pointer hover:border-indigo-500 transition-colors">
                                {/* FIX: Profilbilder in Kommentaren laden den PhotoURL aus der Comment-Datenbank (die beim Posten aktualisiert wurde) */}
                                {c.userPhoto ? <img src={c.userPhoto} className="w-full h-full object-cover"/> : <User className="p-2 w-full h-full text-slate-500"/>}
                            </div>
                            <div className="flex-grow">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2 flex-wrap cursor-pointer" onClick={() => navigate(`/profile/${c.userName || c.userId}`)}>
                                        <span className="font-bold text-white text-sm hover:underline">{c.userName}</span>
                                        <RoleBadge role={c.userRole} />
                                        <span className="text-xs text-slate-500">• {c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}</span>
                                    </div>
                                    {canDelete && <button onClick={() => handleDelete(c.id, c.text)} className="text-slate-600 hover:text-red-400 transition-colors"><Trash2 size={14}/></button>}
                                </div>
                                <p className="text-slate-300 text-sm leading-relaxed">{c.text}</p>
                            </div>
                        </div>
                    );
                })}
                {comments.length === 0 && <p className="text-slate-500 text-sm italic text-center py-4">No comments yet. Be the first!</p>}
            </div>
        </div>
    );
};

const OutfitDetail = ({ outfitId, onBack, allOutfits, navigate, user, currentUserRole }) => {
    const { t } = useContext(LanguageContext);
    const { showToast, confirm } = useContext(UIContext); // use confirm from context
    const [rating, setRating] = useState(0);
    const [hasRated, setHasRated] = useState(false);
    
    const outfit = allOutfits.find(o => o.id === outfitId);

    useEffect(() => {
        const checkRating = async () => {
            if (user && !user.isAnonymous && outfitId) {
                const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'ratings', `${outfitId}_${user.uid}`);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    setHasRated(true);
                    setRating(snap.data().rating);
                }
            }
        };
        checkRating();
    }, [outfitId, user]);

    if (!outfit) {
        return <div className="text-center py-20 text-slate-500">{t.ui.loading}</div>;
    }

    const handleRate = async (stars) => {
        if (!user || user.isAnonymous) { showToast(t.detail.loginToComment, 'error'); return; }
        if (hasRated) { showToast(t.detail.rated, 'error'); return; }
        
        try {
            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'ratings', `${outfit.id}_${user.uid}`), {
                rating: stars,
                userId: user.uid,
                createdAt: serverTimestamp()
            });
            const newCount = (outfit.ratingCount || 0) + 1;
            const currentTotal = (outfit.ratingAvg || 0) * (outfit.ratingCount || 0);
            const newAvg = (currentTotal + stars) / newCount;
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', outfit.id), { ratingAvg: newAvg, ratingCount: newCount });
            setRating(stars);
            setHasRated(true);
            showToast(t.detail.ratingThanks, 'success');
        } catch (e) { console.error(e); }
    };

    const items = outfit.items || [{ name: 'Bundle', price: outfit.price, link: outfit.link }];
    const totalPrice = items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
    const similarOutfits = allOutfits.filter(o => o.id !== outfit.id && o.status === 'approved' && getCategories(o).some(c => getCategories(outfit).includes(c))).slice(0, 4);

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 animate-fade-in">
            <button onClick={() => navigate('/gallery')} className="flex items-center text-slate-400 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-5 h-5 mr-2" /> {t.detail.back}
            </button>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                <div className="space-y-4">
                    <div className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 aspect-[4/5] relative shadow-2xl">
                         <img src={outfit.image} alt={outfit.title} className="w-full h-full object-cover" />
                    </div>
                </div>
                <div className="flex flex-col">
                    <div className="mb-6">
                        <div className="flex justify-between items-start">
                            <h1 className="text-4xl font-extrabold text-white mb-2">{outfit.title}</h1>
                            <div className="flex flex-col items-end">
                                <div className="text-yellow-400 flex items-center gap-1 text-xl font-bold">
                                    <Star fill="currentColor" /> {outfit.ratingAvg ? outfit.ratingAvg.toFixed(1) : '—'}
                                </div>
                                <span className="text-xs text-slate-500">{outfit.ratingCount || 0} Votes</span>
                            </div>
                        </div>
                        {/* Uploader Info */}
                        {outfit.uploaderName && (
                            <div className="flex items-center gap-2 mb-4 text-sm text-slate-400 cursor-pointer hover:text-white transition-colors" onClick={() => navigate(`/profile/${outfit.uploaderName || outfit.userId}`)}>
                                <User size={14}/> {t.detail.uploadedBy} <span className="font-bold text-indigo-400">{outfit.uploaderName}</span>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 mb-4">
                            {getCategories(outfit).map((cat, idx) => (<span key={idx} className="px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-sm font-medium border border-indigo-500/30">{cat}</span>))}
                        </div>
                        <p className="text-slate-400 text-lg leading-relaxed mb-6">{outfit.description}</p>
                        
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 mb-6 flex items-center justify-between">
                            <span className="text-slate-300 text-sm font-bold">{hasRated ? t.detail.rated : t.detail.rate}:</span>
                            <div className="flex gap-2">
                                {[1,2,3,4,5].map(s => (
                                    <button key={s} disabled={hasRated} onClick={() => handleRate(s)} className={`transition-transform hover:scale-125 ${rating >= s ? 'text-yellow-400' : 'text-slate-600 hover:text-yellow-200'} ${hasRated && 'cursor-default hover:scale-100 opacity-80'}`}>
                                        <Star fill={rating >= s ? "currentColor" : "none"} size={24} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
                        <div className="flex justify-between items-end mb-6">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-400" />{t.detail.contains}</h3>
                            <div className="text-right"><p className="text-sm text-slate-400">{t.detail.totalPrice}</p><p className="text-2xl font-bold text-emerald-400">{totalPrice} R$</p></div>
                        </div>
                        <div className="space-y-3">
                            {items.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-slate-900 p-4 rounded-lg border border-slate-800 hover:border-indigo-500/30 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center text-slate-500 font-bold text-xs border border-slate-700">{idx + 1}</div>
                                        <div><p className="font-medium text-white">{item.name}</p><p className="text-xs text-slate-500">Item</p></div>
                                    </div>
                                    <div className="flex items-center gap-4"><span className="text-slate-300 font-medium">{item.price} R$</span><a href={item.link} target="_blank" rel="noopener noreferrer" className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg transition-colors" title={t.detail.buy}><ExternalLink className="w-4 h-4" /></a></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-auto bg-indigo-900/20 border border-indigo-500/20 p-4 rounded-lg flex gap-3">
                        <Info className="w-6 h-6 text-indigo-400 flex-shrink-0" />
                        <div>
                            <h4 className="font-bold text-indigo-300 mb-1">{t.detail.noteTitle}</h4>
                            <p className="text-sm text-indigo-200/70 leading-relaxed">{t.detail.noteDesc}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            <CommentsSection outfitId={outfit.id} user={user} t={t} showToast={showToast} navigate={navigate} confirm={confirm} currentUserRole={currentUserRole} />
            
            {similarOutfits.length > 0 && (
                <div className="border-t border-slate-800 pt-12 mt-12">
                    <h2 className="text-2xl font-bold text-white mb-6">{t.detail.similar}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {similarOutfits.map(sim => (<OutfitCard key={sim.id} outfit={sim} onClick={() => navigate('/outfit/' + sim.id)} />))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Auth = ({ navigate }) => {
    const { t } = useContext(LanguageContext);
    const { showToast } = useContext(UIContext);
    const [isRegistering, setIsRegistering] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const validateUsername = (username) => /^[a-zA-Z0-9._-]+$/.test(username);

    const checkUsernameUnique = async (username) => {
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'profiles'), where('username', '==', username));
        const snap = await getDocs(q);
        return snap.empty;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const email = e.target.email.value;
        const password = e.target.password.value;
        const username = e.target.username?.value;

        try {
            if (isRegistering) {
                if(!validateUsername(username)) throw new Error(t.profile.usernameInvalid);
                const isUnique = await checkUsernameUnique(username);
                if(!isUnique) throw new Error(t.profile.usernameTaken);

                const cred = await createUserWithEmailAndPassword(auth, email, password);
                await updateProfile(cred.user, { displayName: username });
                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', cred.user.uid), {
                    username: username,
                    email: email,
                    role: 'user', 
                    createdAt: serverTimestamp(),
                    photoURL: '',
                    bio: '',
                    robloxLink: '',
                    isBanned: false,
                    lastUsernameChange: serverTimestamp()
                });
                showToast(t.auth.successReg, 'success');
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            navigate('/');
        } catch (err) {
            console.error(err);
            showToast(err.message || t.auth.errorGeneric, 'error');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-[60vh] flex items-center justify-center px-4 animate-fade-in">
            <div className="bg-slate-800 p-8 rounded-xl border border-slate-700 w-full max-w-md shadow-2xl">
                <h2 className="text-2xl font-bold text-white mb-6 text-center">{isRegistering ? t.auth.registerTitle : t.auth.loginTitle}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {isRegistering && (
                        <div>
                            <label className="text-slate-400 text-sm">{t.auth.name}</label>
                            <input name="username" type="text" required className="w-full bg-slate-900 border border-slate-700 rounded p-3 text-white mt-1 focus:border-indigo-500 outline-none" />
                        </div>
                    )}
                    <div>
                        <label className="text-slate-400 text-sm">{t.auth.email}</label>
                        <input name="email" type="email" required className="w-full bg-slate-900 border border-slate-700 rounded p-3 text-white mt-1 focus:border-indigo-500 outline-none" />
                    </div>
                    <div className="relative">
                        <label className="text-slate-400 text-sm">{t.auth.password}</label>
                        <input name="password" type={showPassword ? "text" : "password"} required className="w-full bg-slate-900 border border-slate-700 rounded p-3 text-white mt-1 focus:border-indigo-500 outline-none pr-10" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-slate-400 hover:text-white flex items-center justify-center h-6 w-6">
                            {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                        </button>
                    </div>
                    <button disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold py-3 rounded mt-2 transition-colors shadow-lg shadow-indigo-500/20">
                        {loading ? t.ui.loading : (isRegistering ? t.auth.btnRegister : t.auth.btnLogin)}
                    </button>
                    <button type="button" onClick={() => setIsRegistering(!isRegistering)} className="w-full text-slate-400 text-xs hover:text-indigo-400 mt-2">
                        {isRegistering ? t.auth.switchLog : t.auth.switchReg}
                    </button>
                </form>
            </div>
        </div>
    );
};

const UserProfile = ({ user, userProfile, userRole, profileId, currentUser, navigate, outfits, isSettingsMode }) => {
    const { t } = useContext(LanguageContext);
    const { showToast } = useContext(UIContext);
    const [isEditing, setIsEditing] = useState(false);
    const [targetProfile, setTargetProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // FIX: Extract raw ID if routing included query params
    const cleanProfileId = profileId ? profileId.split('?')[0] : null;
    const isOwnProfile = !cleanProfileId || (currentUser && cleanProfileId === currentUser.uid);
    const actualProfileId = cleanProfileId || currentUser?.uid;

    // Filter outfits uploaded by this user
    const userOutfits = useMemo(() => {
        if(!targetProfile) return [];
        return outfits.filter(o => o.userId === targetProfile.uid && (o.status === 'approved' || (isOwnProfile && o.status !== 'deleted')));
    }, [outfits, targetProfile, isOwnProfile]);

    // Trigger edit mode if settings requested
    useEffect(() => {
        if(isSettingsMode && isOwnProfile) setIsEditing(true);
    }, [isSettingsMode, isOwnProfile]);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!actualProfileId) return;
            // 1. Try fetching by ID
            let snap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', actualProfileId));
            
            // 2. If not found by ID, try fetching by username (only if profileId looks like a username)
            if(!snap.exists()) {
                 const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'profiles'), where('username', '==', actualProfileId));
                 const qSnap = await getDocs(q);
                 if(!qSnap.empty) snap = qSnap.docs[0];
            }

            if (snap.exists()) setTargetProfile({ uid: snap.id, ...snap.data() });
            setLoading(false);
        };
        fetchProfile();
    }, [actualProfileId]);

    const handleCopyId = () => {
        if(targetProfile && targetProfile.uid) {
             // FIX: Copy ID Button Position (integrated next to username)
             navigator.clipboard.writeText(targetProfile.uid);
             showToast(t.profile.idCopied, 'success');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const displayName = e.target.displayName.value;
        const bio = e.target.bio.value;
        const robloxLink = e.target.robloxLink.value;
        const photoFile = e.target.photoFile.files[0];
        let photoURL = targetProfile.photoURL;

        // Basic Roblox Link Validation
        if(robloxLink && !robloxLink.includes('roblox.com')) return showToast('Invalid Roblox URL', 'error');
        
        if(displayName !== targetProfile.username) {
            // Username change logic
             const valid = /^[a-zA-Z0-9._-]+$/.test(displayName);
             if(!valid) return showToast(t.profile.usernameInvalid, 'error');

             // Cooldown check
             if(!isAdmin) {
                 const lastChange = targetProfile.lastUsernameChange?.seconds * 1000 || 0;
                 const now = Date.now();
                 const daysDiff = (now - lastChange) / (1000 * 60 * 60 * 24);
                 if(daysDiff < 14 && lastChange > 0) return showToast(t.profile.cooldown + new Date(lastChange + 14*86400000).toLocaleDateString(), 'error');
             }

             // Uniqueness check
             const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'profiles'), where('username', '==', displayName));
             const snap = await getDocs(q);
             if(!snap.empty && snap.docs[0].id !== currentUser.uid) return showToast(t.profile.usernameTaken, 'error');
        }

        if (photoFile) {
            if (photoFile.size > 500 * 1024) return showToast(t.ui.maxSize, 'error');
            const reader = new FileReader();
            reader.readAsDataURL(photoFile);
            await new Promise(resolve => {
                reader.onloadend = () => { photoURL = reader.result; resolve(); };
            });
        }

        try {
            // Store image data ONLY in Firestore
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', currentUser.uid), {
                username: displayName,
                bio: bio,
                photoURL: photoURL,
                robloxLink: robloxLink,
                lastUsernameChange: displayName !== targetProfile.username ? serverTimestamp() : targetProfile.lastUsernameChange || serverTimestamp()
            });
            // Update Auth Display Name (Safe, no huge data)
            await updateProfile(currentUser, { displayName });
            
            setTargetProfile({...targetProfile, username: displayName, bio, photoURL, robloxLink});
            setIsEditing(false);
            showToast('Profile updated!', 'success');
        } catch (err) { console.error(err); showToast('Error updating profile', 'error'); }
    };

    if (loading) return <div className="text-center py-20 text-slate-500">{t.ui.loading}</div>;
    if (!targetProfile) return <div className="text-center py-20 text-slate-500">User not found</div>;

    const displayRole = targetProfile.role || 'user';
    const isOwner = targetProfile.email === SUPER_ADMINS[0]; 
    const isBanned = targetProfile.isBanned;
    // FIX: Use current user role for admin check, not target profile role
    const isAdmin = userRole === 'admin'; 

    const renderBio = (text) => {
        if (!isOwner || !text) return text;
        const parts = text.split('BloxStyle');
        if (parts.length === 1) return text;
        return (
            <span>
                {parts[0]}
                <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">BloxStyle</span>
                {parts[1]}
            </span>
        );
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12 animate-fade-in">
            {isEditing && isOwnProfile ? (
               <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700 shadow-xl mb-8">
                   <h2 className="text-2xl font-bold text-white mb-6">{t.profile.settingsTitle}</h2>
                   <form onSubmit={handleUpdate} className="space-y-4">
                        <div>
                            <label className="block text-xs text-slate-400 mb-1">Username</label>
                            <input name="displayName" defaultValue={targetProfile.username} className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white" />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-400 mb-1">Bio</label>
                            <textarea name="bio" rows="3" defaultValue={targetProfile.bio} placeholder={t.profile.bioPlaceholder} className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white" />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-400 mb-1">{t.profile.robloxLink}</label>
                            <input name="robloxLink" defaultValue={targetProfile.robloxLink} placeholder={t.profile.robloxPlaceholder} className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white" />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-400 mb-2">{t.profile.uploadImg}</label>
                            <label className="flex items-center gap-3 cursor-pointer bg-slate-900 border border-slate-700 hover:border-indigo-500 p-3 rounded transition-colors w-full md:w-auto justify-center md:justify-start">
                                <input type="file" name="photoFile" accept="image/*" className="hidden" onChange={(e) => { if(e.target.files[0]) showToast('Image selected', 'success') }} />
                                <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center border border-slate-600">
                                    <Camera className="text-slate-400" size={20} />
                                </div>
                                <span className="text-sm text-slate-300 font-medium">{t.ui.clickToUpload}</span>
                            </label>
                        </div>
                        <div className="flex gap-2 justify-end mt-4">
                            <button type="button" onClick={() => setIsEditing(false)} className="text-slate-400 px-4 py-2 hover:text-white">{t.ui.cancelBtn}</button>
                            <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold">{t.profile.save}</button>
                        </div>
                    </form>
               </div>
            ) : (
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 shadow-xl mb-8 flex flex-col md:flex-row gap-8 items-center md:items-start relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                        <BloxLogo className="w-64 h-64 text-indigo-500"/>
                    </div>

                    {isBanned && <div className="absolute top-4 right-4 bg-red-600 text-white font-bold px-4 py-1 rounded shadow-lg z-20">BANNED</div>}

                    {/* Roblox Link Icon (Top Right) */}
                    {targetProfile.robloxLink && (
                        <a href={targetProfile.robloxLink} target="_blank" rel="noopener noreferrer" className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors bg-slate-800/50 p-2 rounded-full backdrop-blur-sm border border-slate-600 hover:border-white z-20" title={t.profile.robloxBtn}>
                             <img src="/roblox_logo.png" alt="Roblox" className="w-6 h-6 object-contain" />
                        </a>
                    )}
                    
                    <div className="w-32 h-32 rounded-full bg-slate-700 overflow-hidden border-4 border-slate-600 flex-shrink-0 z-10 shadow-lg">
                        {targetProfile.photoURL ? <img src={targetProfile.photoURL} className="w-full h-full object-cover" /> : <User className="w-full h-full p-6 text-slate-400" />}
                    </div>
                    <div className="flex-grow text-center md:text-left w-full z-10">
                        <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                            <h2 className="text-3xl font-bold text-white">{targetProfile.username || 'User'}</h2>
                             {/* Copy ID Button for Admins Next to Name */}
                            {isAdmin && (
                                <button onClick={handleCopyId} className="text-slate-500 hover:text-white transition-colors flex items-center gap-1 text-sm bg-slate-700 px-3 py-1 rounded-full ml-2" title={t.profile.copyId}>
                                    <Copy size={14} /> ID
                                </button>
                            )}
                        </div>
                        
                        {isOwnProfile ? (
                            <p className="text-slate-400 text-sm mb-4">{targetProfile.email}</p>
                        ) : null}

                        <p className="text-slate-300 text-sm mb-6 max-w-lg mx-auto md:mx-0 leading-relaxed italic">
                            {targetProfile.bio ? renderBio(targetProfile.bio) : t.profile.noBio}
                        </p>

                        <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border 
                                ${displayRole === 'admin' ? 'bg-red-500/20 text-red-300 border-red-500/30' : 
                                  displayRole === 'mod' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                                  displayRole === 'vip' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                                  'bg-slate-700 text-slate-300 border-slate-600'}`}>
                                {displayRole}
                            </span>
                            
                            <span className="text-slate-500 text-sm flex items-center bg-slate-900/50 px-3 py-1 rounded-full border border-slate-800 gap-1">
                                <Calendar size={12}/> {t.profile.joined}: {targetProfile.createdAt ? new Date(targetProfile.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                            </span>
                        </div>
                        {isOwnProfile && <button onClick={() => setIsEditing(true)} className="text-indigo-400 hover:text-white text-sm font-medium flex items-center gap-1 mx-auto md:mx-0 border border-indigo-500/30 px-4 py-2 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 transition-all"><Edit2 size={14} /> {t.profile.edit}</button>}
                    </div>
                </div>
            )}
            
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2"><Layers className="text-indigo-500"/> {t.profile.uploads}</h3>
            {userOutfits.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {userOutfits.map(o => (<OutfitCard key={o.id} outfit={o} onClick={() => navigate('/outfit/' + o.id)} />))}
                </div>
            ) : (
                <div className="bg-slate-800/50 border border-dashed border-slate-700 rounded-xl p-12 text-center text-slate-500">
                    <Upload className="w-12 h-12 mx-auto mb-4 opacity-50"/>
                    <p>{t.profile.noUploads}</p>
                </div>
            )}
        </div>
    );
};

const SubmitOutfit = ({ categories, user, navigate }) => {
    const { t } = useContext(LanguageContext);
    const { showToast } = useContext(UIContext);
    const [status, setStatus] = useState('idle');
    const [items, setItems] = useState([{ name: '', price: '', link: '' }]);
    const [selCats, setSelCats] = useState([]);
    const [imgUrl, setImgUrl] = useState('');
    
    // Auth Check
    if (!user || user.isAnonymous) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-4">
                <Lock size={48} className="text-slate-600"/>
                <h2 className="text-2xl font-bold text-white">{t.auth.loginReq}</h2>
                <button onClick={() => navigate('/login')} className="bg-indigo-600 text-white px-6 py-2 rounded-full font-bold">{t.auth.btnLogin}</button>
            </div>
        );
    }
    
    const addItem = () => setItems([...items, { name: '', price: '', link: '' }]);
    const removeItem = (index) => {
        if(items.length > 1) setItems(items.filter((_, i) => i !== index));
    };
    
    const handleItem = (idx, f, v) => { const n = [...items]; n[idx][f] = v; setItems(n); };
    const toggleCat = (c) => setSelCats(selCats.includes(c) ? selCats.filter(x => x !== c) : [...selCats, c]);

    // Handle File Upload for Main Image
    const handleImageFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 500 * 1024) return showToast(t.ui.maxSize, 'error');
            const reader = new FileReader();
            reader.onloadend = () => setImgUrl(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const title = e.target.title.value;
        const description = e.target.description.value;

        // Validation
        if(!title) return showToast(t.submit.reqTitle, 'error');
        if(!imgUrl) return showToast(t.submit.reqImg, 'error');
        if(selCats.length === 0) return showToast(t.submit.reqCat, 'error');
        
        for(let item of items) {
            if(!item.name || !item.price || !item.link) return showToast(t.submit.reqItems, 'error');
            if(!item.link.startsWith("https://www.roblox.com/catalog/")) return showToast(t.submit.reqLink, 'error');
        }
        
        setStatus('submitting');
        try {
            let uploaderName = user.displayName || 'User';
            // Try fetch latest profile name
            const p = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', user.uid));
            if(p.exists()) uploaderName = p.data().username;

            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'outfits'), {
                title, 
                description, 
                categories: selCats, 
                image: imgUrl, 
                items: items, 
                status: 'pending', 
                ratingAvg: 0,
                ratingCount: 0,
                userId: user.uid,
                uploaderName: uploaderName,
                createdAt: serverTimestamp()
            });
            
            // DISCORD LOG
            sendToDiscord(WEBHOOK_CREATED, "New Outfit Submitted", `Title: ${title}\nUser: ${uploaderName}`, 5763719);
            
            setStatus('success'); 
            e.target.reset(); 
            setItems([{ name: '', price: '', link: '' }]); 
            setSelCats([]); 
            setImgUrl('');
        } catch (err) { 
            setStatus('error'); 
            showToast('Upload failed', 'error');
        }
    };

    if (status === 'success') return (
        <div className="text-center py-20 animate-fade-in">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4"/>
            <h2 className="text-3xl text-white font-bold mb-2">{t.submit.successTitle}</h2>
            <p className="text-slate-400 mb-6">{t.submit.successDesc}</p>
            <button onClick={() => setStatus('idle')} className="text-indigo-400 underline hover:text-white">{t.submit.btnMore}</button>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto px-4 py-12 animate-fade-in">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">{t.submit.title}</h2>
            
            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* LEFT COLUMN: Basic Info */}
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl space-y-6">
                    <div>
                        <label className="text-xs text-slate-400 uppercase font-bold mb-1 block">{t.submit.formTitle} *</label>
                        <input name="title" className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded focus:border-indigo-500 outline-none" required />
                    </div>

                    <div>
                         <label className="text-xs text-slate-400 uppercase font-bold mb-1 block">{t.submit.imgLabel} *</label>
                         <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 hover:bg-slate-800/50 transition-colors relative group">
                             {!imgUrl ? (
                                 <label className="flex flex-col items-center justify-center cursor-pointer h-40">
                                     <input type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
                                     <ImagePlus className="text-slate-400 mb-2 group-hover:text-indigo-400 transition-colors" size={32} />
                                     <span className="text-sm text-slate-400 font-medium">{t.ui.clickToUpload}</span>
                                 </label>
                             ) : (
                                 <div className="relative h-64 w-full">
                                     <img src={imgUrl} className="w-full h-full object-cover rounded" alt="Preview"/>
                                     <button type="button" onClick={() => setImgUrl('')} className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white p-2 rounded-full backdrop-blur-sm transition-colors">
                                         <Trash2 size={16}/>
                                     </button>
                                 </div>
                             )}
                         </div>
                         {/* Fallback URL Input */}
                         <div className="mt-2 flex items-center gap-2">
                             <span className="text-xs text-slate-500">OR</span>
                             <input value={imgUrl} onChange={e => setImgUrl(e.target.value)} placeholder="Paste Image URL directly" className="flex-grow bg-slate-900 border border-slate-700 text-white p-2 rounded text-xs focus:border-indigo-500 outline-none" />
                         </div>
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 uppercase font-bold mb-2 block">{t.submit.formCat} *</label>
                        <div className="flex flex-wrap gap-2">
                            {categories.map(c => (
                                <button type="button" key={c} onClick={() => toggleCat(c)} className={`px-4 py-2 text-xs font-medium rounded-full border transition-all ${selCats.includes(c) ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'}`}>{c}</button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 uppercase font-bold mb-1 block">{t.submit.formDesc}</label>
                        <textarea name="description" rows="4" className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded focus:border-indigo-500 outline-none" />
                    </div>
                </div>

                {/* RIGHT COLUMN: Items */}
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <label className="text-xs text-slate-400 uppercase font-bold">{t.submit.itemLabel} *</label>
                        <span className="text-xs text-slate-500">{items.length} Items</span>
                    </div>

                    <div className="flex-grow space-y-3 overflow-y-auto pr-2 max-h-[600px] custom-scrollbar">
                        {items.map((it, i) => (
                            <div key={i} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50 hover:border-indigo-500/30 transition-colors relative group">
                                {items.length > 1 && (
                                    <button type="button" onClick={() => removeItem(i)} className="absolute top-1 right-1 text-slate-500 hover:text-red-400 p-1">
                                        <MinusCircle size={14} />
                                    </button>
                                )}
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    <div className="col-span-2">
                                        <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Name</label>
                                        <input placeholder="Item Name" value={it.name} onChange={e => handleItem(i, 'name', e.target.value)} className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded text-sm focus:border-indigo-500 outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Price</label>
                                        <div className="relative">
                                            <input placeholder="0" type="number" value={it.price} onChange={e => handleItem(i, 'price', e.target.value)} className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded text-sm pl-8 focus:border-indigo-500 outline-none" />
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">R$</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Roblox Link</label>
                                    <input placeholder="https://www.roblox.com/catalog/..." value={it.link} onChange={e => handleItem(i, 'link', e.target.value)} className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded text-sm focus:border-indigo-500 outline-none" />
                                </div>
                            </div>
                        ))}
                    </div>

                    <button type="button" onClick={addItem} className="w-full py-3 mt-4 bg-slate-700/50 hover:bg-slate-700 text-indigo-300 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors border border-dashed border-slate-600 hover:border-indigo-500">
                        <Plus size={18}/> {t.submit.addItem}
                    </button>
                </div>

                {/* SUBMIT BUTTON */}
                <div className="lg:col-span-2">
                    <button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-[1.01]">
                        {status === 'submitting' ? t.ui.loading : t.submit.btnSubmit}
                    </button>
                </div>
            </form>
        </div>
    );
};

const Contact = () => {
    const { t } = useContext(LanguageContext);
    const { showToast } = useContext(UIContext);
    
    const sendContact = (e) => {
        e.preventDefault();
        showToast(t.contact.success, 'success');
        e.target.reset();
    };

    return (
        <div className="max-w-2xl mx-auto px-4 py-12 animate-fade-in">
            <h2 className="text-3xl font-bold text-white mb-2 text-center">{t.contact.title}</h2>
            <p className="text-slate-400 text-center mb-8">{t.contact.desc}</p>
            <form onSubmit={sendContact} className="bg-slate-800 p-8 rounded-xl border border-slate-700 shadow-xl space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-xs text-slate-400 font-bold mb-1 block">{t.contact.name}</label><input required className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded" /></div>
                    <div><label className="text-xs text-slate-400 font-bold mb-1 block">{t.contact.email}</label><input type="email" required className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded" /></div>
                </div>
                <div><label className="text-xs text-slate-400 font-bold mb-1 block">{t.contact.msg}</label><textarea rows="4" required className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded" /></div>
                <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded">{t.contact.send}</button>
            </form>
        </div>
    );
};

const TOS = () => {
    const { t } = useContext(LanguageContext);
    return (
        <div className="max-w-2xl mx-auto px-4 py-12 animate-fade-in">
             <h2 className="text-3xl font-bold text-white mb-6 text-center">{t.tos.title}</h2>
             <div className="bg-slate-800 p-8 rounded-xl border border-slate-700 text-slate-300 whitespace-pre-line">
                 {t.tos.content}
             </div>
        </div>
    );
}

// --- EDIT OUTFIT MODAL ---
const EditOutfitModal = ({ outfit, onClose, onSave, categories, t }) => {
    const [title, setTitle] = useState(outfit.title);
    const [desc, setDesc] = useState(outfit.description);
    const [img, setImg] = useState(outfit.image);
    const [cats, setCats] = useState(getCategories(outfit));
    const [items, setItems] = useState(outfit.items || []);

    const toggleCat = (c) => setCats(cats.includes(c) ? cats.filter(x=>x!==c) : [...cats, c]);
    const handleItem = (idx, f, v) => { const n = [...items]; n[idx][f] = v; setItems(n); };
    const addItem = () => setItems([...items, { name: '', price: '', link: '' }]);
    const delItem = (idx) => setItems(items.filter((_, i) => i !== idx));

    const handleSave = () => {
        onSave({ ...outfit, title, description: desc, image: img, categories: cats, items: items });
    };

    return (
        <div className="fixed inset-0 bg-slate-950/90 z-[100] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-slate-800 w-full max-w-3xl rounded-xl border border-slate-700 shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2"><Edit2 size={24} className="text-indigo-500"/> {t.admin.editOutfit}</h2>
                    <button onClick={onClose}><X size={24} className="text-slate-400 hover:text-white"/></button>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-slate-400 text-xs uppercase font-bold mb-1">Title</label>
                            <input value={title} onChange={e=>setTitle(e.target.value)} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded"/>
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs uppercase font-bold mb-1">Description</label>
                            <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows="3" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded"/>
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs uppercase font-bold mb-1">Image URL</label>
                            <input value={img} onChange={e=>setImg(e.target.value)} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded"/>
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs uppercase font-bold mb-2">Categories</label>
                            <div className="flex flex-wrap gap-2">
                                {categories.map(c => (
                                    <button key={c} onClick={()=>toggleCat(c)} className={`px-2 py-1 text-xs rounded border ${cats.includes(c) ? 'bg-indigo-600 text-white border-indigo-500' : 'text-slate-400 border-slate-600'}`}>{c}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    {/* ITEMS EDIT */}
                    <div className="bg-slate-900 p-4 rounded border border-slate-800">
                        <label className="block text-slate-400 text-xs uppercase font-bold mb-3">{t.admin.itemsEdit}</label>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                            {items.map((it, i) => (
                                <div key={i} className="bg-slate-800/50 p-2 rounded border border-slate-700/50 relative group">
                                    <div className="flex gap-2 mb-2">
                                        <input value={it.name} onChange={e => handleItem(i, 'name', e.target.value)} className="bg-slate-900 border border-slate-600 text-white p-1 rounded w-full text-xs" placeholder="Name" />
                                        <input value={it.price} onChange={e => handleItem(i, 'price', e.target.value)} className="bg-slate-900 border border-slate-600 text-white p-1 rounded w-16 text-xs" placeholder="R$" />
                                    </div>
                                    <input value={it.link} onChange={e => handleItem(i, 'link', e.target.value)} className="bg-slate-900 border border-slate-600 text-white p-1 rounded w-full text-xs" placeholder="Link" />
                                    <button onClick={() => delItem(i)} className="absolute top-1 right-1 text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12}/></button>
                                </div>
                            ))}
                        </div>
                        <button onClick={addItem} className="text-indigo-400 text-xs flex items-center gap-1 mt-2 hover:text-white"><Plus size={12}/> {t.admin.itemAdd}</button>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-8">
                    <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white">{t.admin.discard}</button>
                    <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold">{t.admin.saveChanges}</button>
                </div>
            </div>
        </div>
    );
};

// --- ADMIN DASHBOARD ---
const AdminDashboard = ({ outfits, pendingOutfits, rejectedOutfits, user, userRole, categories, setCategories, maintenanceMode, setMaintenanceMode, announcement, setAnnouncement }) => {
    const { t } = useContext(LanguageContext);
    const { showToast, confirm } = useContext(UIContext);
    const [tab, setTab] = useState('manage');
    const [editingOutfit, setEditingOutfit] = useState(null);
    
    // Sorting States
    const [userSort, setUserSort] = useState({ key: 'createdAt', dir: 'desc' });
    const [outfitSort, setOutfitSort] = useState({ key: 'createdAt', dir: 'desc' });
    const [rejectSort, setRejectSort] = useState({ key: 'createdAt', dir: 'desc' });

    // User List State (Admin Only)
    const [allUsers, setAllUsers] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    
    // Announcement State (Admin Only)
    const [annTitle, setAnnTitle] = useState(announcement?.title || '');
    const [annMsg, setAnnMsg] = useState(announcement?.message || '');

    const isAdmin = userRole === 'admin';
    const isMod = userRole === 'mod';

    // Helper Sort Function
    const handleSort = (key, currentSort, setSort) => {
        const dir = currentSort.key === key && currentSort.dir === 'asc' ? 'desc' : 'asc';
        setSort({ key, dir });
    };

    // Fetch all users only if tab is 'roles' AND user is admin
    useEffect(() => {
        if(tab === 'roles' && isAdmin) {
            const fetchUsers = async () => {
                try {
                    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'profiles'), limit(100));
                    const snapshot = await getDocs(q);
                    setAllUsers(snapshot.docs.map(d => ({id: d.id, ...d.data()})));
                } catch(e) { console.error("Could not fetch users", e); }
            };
            fetchUsers();
        }
    }, [tab, isAdmin]);

    const handleSetAnnouncement = async () => {
        const data = { title: annTitle, message: annMsg };
        setAnnouncement(data);
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config'), { announcement: data }, { merge: true });
        showToast('Announcement Updated', 'success');
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', userId), { role: newRole });
            setAllUsers(allUsers.map(u => u.id === userId ? {...u, role: newRole} : u));
            showToast(`Role updated to ${newRole}`, 'success');
        } catch(e) { console.error(e); showToast('Role update failed', 'error'); }
    };
    
    const handleBanToggle = async (user) => {
        const newBanStatus = !user.isBanned;
        confirm(newBanStatus ? t.admin.confirmBan : 'Unban user?', async () => {
            try {
                await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'profiles', user.id), { isBanned: newBanStatus });
                setAllUsers(allUsers.map(u => u.id === user.id ? {...u, isBanned: newBanStatus} : u));
                showToast(newBanStatus ? 'User Banned' : 'User Unbanned', 'success');
            } catch(e) { showToast('Action failed', 'error'); }
        });
    };

    const handleApprove = async (id) => { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', id), { status: 'approved' }); showToast('Approved', 'success'); };
    
    const handleReject = async (outfit) => { 
        confirm(t.ui.confirmReject, async () => { 
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', outfit.id), { status: 'rejected' });
            // DISCORD LOG
            sendToDiscord(WEBHOOK_REJECTED, "Outfit Rejected", `Title: ${outfit.title}`, 15158332);
        }); 
    };
    
    const handleDelete = async (id) => { 
        confirm(t.ui.confirmDelete, async () => { 
             await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', id));
             // DISCORD LOG
             sendToDiscord(WEBHOOK_DELETED, "Outfit Permanently Deleted", `ID: ${id}`, 15548997);
        }); 
    };
    
    // SOFT DELETE (TRASH)
    const handleTrash = async (outfit) => { 
        confirm(t.ui.confirmTrash, async () => { 
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', outfit.id), { status: 'rejected' }); 
            showToast('Moved to trash', 'info');
            // DISCORD LOG
            sendToDiscord(WEBHOOK_REJECTED, "Outfit Trashed", `Title: ${outfit.title}`, 15158332);
        }); 
    };
    
    // RESTORE
    const handleRestore = async (id) => {
        confirm(t.ui.confirmRestore, async () => {
             await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', id), { status: 'approved' }); 
             showToast('Restored', 'success');
        });
    }

    const toggleMaintenance = async () => { 
        const ns = !maintenanceMode; setMaintenanceMode(ns); 
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config'), { maintenanceMode: ns }, { merge: true }); 
    };

    const handleEditSave = async (updatedData) => {
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'outfits', updatedData.id), updatedData);
            showToast('Outfit updated successfully', 'success');
            setEditingOutfit(null);
        } catch(e) { console.error(e); showToast('Update failed', 'error'); }
    };

    // Filter & Sort Users
    const processedUsers = useMemo(() => {
        let users = allUsers.filter(u => (
            u.username?.toLowerCase().includes(userSearch.toLowerCase()) || 
            u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.id?.toLowerCase().includes(userSearch.toLowerCase())
        ));
        users.sort((a,b) => {
            let valA = a[userSort.key] || '';
            let valB = b[userSort.key] || '';
            if(userSort.key === 'createdAt') {
                valA = a.createdAt?.seconds || 0;
                valB = b.createdAt?.seconds || 0;
            }
            if (valA < valB) return userSort.dir === 'asc' ? -1 : 1;
            if (valA > valB) return userSort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return users;
    }, [allUsers, userSearch, userSort]);

    // Helper to Sort Outfits
    const sortOutfits = (list, sortState) => {
        return [...list].sort((a,b) => {
            let valA = a[sortState.key] || '';
            let valB = b[sortState.key] || '';
            
            if(sortState.key === 'price') {
                valA = a.items?.reduce((s,i)=>s+Number(i.price),0) || 0;
                valB = b.items?.reduce((s,i)=>s+Number(i.price),0) || 0;
            }
            if(sortState.key === 'createdAt') {
                valA = a.createdAt?.seconds || 0;
                valB = b.createdAt?.seconds || 0;
            }
            
            if (valA < valB) return sortState.dir === 'asc' ? -1 : 1;
            if (valA > valB) return sortState.dir === 'asc' ? 1 : -1;
            return 0;
        });
    }

    const processedOutfits = useMemo(() => sortOutfits(outfits, outfitSort), [outfits, outfitSort]);
    const processedRejected = useMemo(() => sortOutfits(rejectedOutfits, rejectSort), [rejectedOutfits, rejectSort]);

    // Available Tabs based on Role
    const tabs = ['manage', 'reviews'];
    if(isAdmin) { tabs.push('rejected', 'roles'); }

    if (userRole !== 'admin' && userRole !== 'mod') return <div className="text-white p-20 text-center"><ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-500"/>{t.admin.accessDenied}</div>;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 animate-fade-in">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    {t.admin.title} 
                    <RoleBadge role={userRole} />
                </h1>
                {isAdmin && (
                    <button onClick={toggleMaintenance} className={`px-4 py-2 rounded font-bold border flex items-center gap-2 ${maintenanceMode ? 'border-red-500 text-red-400 bg-red-500/10' : 'border-green-500 text-green-400 bg-green-500/10'}`}>
                        <Power size={16}/> {maintenanceMode ? t.admin.maintenanceOn : t.admin.maintenanceOff}
                    </button>
                )}
            </div>
            
            {/* Announcement Section (Admin Only) */}
            {isAdmin && (
                <div className="bg-slate-800 rounded-2xl border border-slate-700 mb-10 shadow-xl overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-bl-full z-0"></div>
                    <div className="p-6 relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-indigo-500/20 p-2 rounded-lg">
                                <Megaphone size={24} className="text-indigo-400"/>
                            </div>
                            <h3 className="text-xl font-bold text-white">{t.admin.announceTitle}</h3>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-slate-400 uppercase font-bold mb-1 block">Headline</label>
                                <input value={annTitle} onChange={e => setAnnTitle(e.target.value)} placeholder={t.admin.announcePlaceholderTitle} className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded-lg focus:border-indigo-500 outline-none" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 uppercase font-bold mb-1 block">Content</label>
                                <textarea value={annMsg} onChange={e => setAnnMsg(e.target.value)} placeholder={t.admin.announcePlaceholderMsg} rows="3" className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded-lg focus:border-indigo-500 outline-none" />
                            </div>
                            <div className="flex gap-3 justify-end pt-2">
                                 <button onClick={() => { setAnnouncement(null); setAnnTitle(''); setAnnMsg(''); handleSetAnnouncement(); }} className="text-slate-400 hover:text-red-400 px-4 py-2 text-sm font-medium transition-colors">
                                    {t.admin.announceDel}
                                </button>
                                <button onClick={handleSetAnnouncement} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-bold shadow-lg transition-all">
                                    {t.admin.announceSet}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-slate-700 pb-2 overflow-x-auto">
                {tabs.map(tabKey => (
                    <button key={tabKey} onClick={() => setTab(tabKey)} className={`px-4 py-2 font-medium capitalize whitespace-nowrap transition-colors ${tab === tabKey ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-slate-400 hover:text-white'}`}>
                        {t.admin['tab' + tabKey.charAt(0).toUpperCase() + tabKey.slice(1)]}
                        {tabKey === 'reviews' && pendingOutfits.length > 0 && <span className="ml-2 bg-red-500 text-white text-[10px] px-1.5 rounded-full">{pendingOutfits.length}</span>}
                    </button>
                ))}
            </div>

            {/* MANAGE (ACTIVE OUTFITS) */}
            {tab === 'manage' && (
                <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700">
                    <table className="w-full text-left text-slate-300">
                        <thead className="bg-slate-900 text-xs uppercase cursor-pointer">
                            <tr>
                                <th className="p-4">{t.admin.thImg}</th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('title', outfitSort, setOutfitSort)}>{t.admin.thTitle} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('uploaderName', outfitSort, setOutfitSort)}>{t.admin.thUploader} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('price', outfitSort, setOutfitSort)}>{t.admin.thPrice} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('createdAt', outfitSort, setOutfitSort)}>{t.admin.thDate} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('status', outfitSort, setOutfitSort)}>{t.admin.thStatus} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 text-right">{t.admin.thActions}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {processedOutfits.map(o => (
                                <tr key={o.id} className="hover:bg-slate-700/50">
                                    <td className="p-4"><img src={o.image} className="w-10 h-10 rounded object-cover"/></td>
                                    <td className="p-4 font-bold text-white">{o.title}</td>
                                    <td className="p-4 text-xs">{o.uploaderName || 'Unknown'}</td>
                                    <td className="p-4 text-xs font-mono">{o.items?.reduce((s,i)=>s+Number(i.price),0)} R$</td>
                                    <td className="p-4 text-xs text-slate-400">{new Date(o.createdAt?.seconds*1000).toLocaleDateString()}</td>
                                    <td className="p-4">
                                        <span className="text-xs px-2 py-1 rounded border bg-green-500/20 text-green-400 border-green-500/30">
                                            Active
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => handleTrash(o)} className="text-red-400 hover:bg-red-500/20 p-2 rounded"><Trash2 size={16}/></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* REVIEWS (PENDING) */}
            {tab === 'reviews' && (
                <div className="space-y-4">
                    {pendingOutfits.length === 0 && <p className="text-slate-500 text-center py-10 bg-slate-800 rounded-xl border border-dashed border-slate-700">No pending reviews.</p>}
                    {pendingOutfits.map(o => (
                        <div key={o.id} className="bg-slate-800 p-4 rounded-xl border border-slate-700 flex flex-col md:flex-row gap-4">
                            <img src={o.image} className="w-full md:w-32 h-40 object-cover rounded bg-slate-900"/>
                            <div className="flex-grow">
                                <div className="flex justify-between">
                                    <h3 className="font-bold text-white text-lg">{o.title}</h3>
                                    <span className="text-xs text-slate-500">by {o.uploaderName}</span>
                                </div>
                                <p className="text-sm text-slate-400 mb-2">{o.description}</p>
                                <div className="flex flex-wrap gap-2 mb-4">{getCategories(o).map(c => <span key={c} className="text-xs bg-indigo-900 text-indigo-200 px-2 py-0.5 rounded">{c}</span>)}</div>
                                <div className="flex gap-2">
                                    <button onClick={() => setEditingOutfit(o)} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-1"><Edit2 size={14}/> Edit</button>
                                    <button onClick={() => handleApprove(o.id)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-1"><CheckCircle size={14}/> Approve</button>
                                    <button onClick={() => handleReject(o)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-1"><X size={14}/> Reject</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* REJECTED (ADMIN ONLY) */}
            {tab === 'rejected' && isAdmin && (
                <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700">
                     <table className="w-full text-left text-slate-300">
                        <thead className="bg-slate-900 text-xs uppercase cursor-pointer">
                            <tr>
                                <th className="p-4" onClick={() => handleSort('image', rejectSort, setRejectSort)}>{t.admin.thImg}</th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('title', rejectSort, setRejectSort)}>{t.admin.thTitle} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('uploaderName', rejectSort, setRejectSort)}>{t.admin.thUploader} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('price', rejectSort, setRejectSort)}>{t.admin.thPrice} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('createdAt', rejectSort, setRejectSort)}>{t.admin.thDate} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 text-right">{t.admin.thActions}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {processedRejected.map(o => (
                                <tr key={o.id} className="opacity-75">
                                    <td className="p-4"><img src={o.image} className="w-10 h-10 rounded object-cover grayscale"/></td>
                                    <td className="p-4 font-bold text-white">{o.title}</td>
                                    <td className="p-4 text-xs">{o.uploaderName}</td>
                                    <td className="p-4 text-xs font-mono">{o.items?.reduce((s,i)=>s+Number(i.price),0)} R$</td>
                                    <td className="p-4 text-xs text-slate-400">{new Date(o.createdAt?.seconds*1000).toLocaleDateString()}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => handleRestore(o.id)} className="text-green-400 hover:bg-green-500/20 p-2 rounded mr-2" title="Restore"><RotateCcw size={18}/></button>
                                        <button onClick={() => handleDelete(o.id)} className="text-red-400 hover:bg-red-500/20 p-2 rounded" title="Delete Permanently"><Trash2 size={18}/></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ROLES (ADMIN ONLY) */}
            {tab === 'roles' && isAdmin && (
                <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700">
                    <div className="p-4 border-b border-slate-700 flex flex-col md:flex-row justify-between items-center bg-slate-900/50 gap-4">
                        <h3 className="text-white font-bold flex items-center gap-2"><Users size={18}/> User List</h3>
                        <div className="flex gap-2 w-full md:w-auto">
                            <div className="relative flex-grow md:flex-grow-0">
                                <Search size={14} className="absolute left-2 top-2 text-slate-500"/>
                                <input placeholder={t.admin.searchUser} value={userSearch} onChange={e=>setUserSearch(e.target.value)} className="bg-slate-800 border border-slate-600 rounded text-sm py-1 pl-7 pr-2 text-white w-full"/>
                            </div>
                            <div className="relative">
                                <select value={userSort.key} onChange={e=>setUserSort({key: e.target.value, dir: userSort.dir})} className="bg-slate-800 border border-slate-600 rounded text-sm py-1 pl-2 pr-6 text-white appearance-none">
                                    <option value="username">{t.admin.sortName}</option>
                                    <option value="role">{t.admin.sortRole}</option>
                                    <option value="createdAt">{t.admin.sortDate}</option>
                                </select>
                                <Filter size={12} className="absolute right-2 top-2 text-slate-500 pointer-events-none"/>
                            </div>
                        </div>
                    </div>
                    <table className="w-full text-left text-slate-300">
                        <thead className="bg-slate-900 text-xs uppercase cursor-pointer">
                            <tr>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('username', userSort, setUserSort)}>{t.admin.thUser} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('role', userSort, setUserSort)}>{t.admin.thRole} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4 hover:text-white" onClick={() => handleSort('createdAt', userSort, setUserSort)}>{t.admin.thDate} <ArrowUpDown size={12} className="inline"/></th>
                                <th className="p-4">{t.admin.thActions}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {processedUsers.map(u => (
                                <tr key={u.id} className={`hover:bg-slate-700/30 ${u.isBanned ? 'bg-red-900/10' : ''}`}>
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-slate-700 overflow-hidden border border-slate-600">{u.photoURL ? <img src={u.photoURL} className="w-full h-full object-cover"/> : <User className="p-1.5"/>}</div>
                                            <div>
                                                <div className="font-bold text-white text-sm flex items-center gap-2">
                                                    {u.username || 'No Name'}
                                                    {u.isBanned && <span className="text-[10px] bg-red-500 text-white px-1 rounded">BANNED</span>}
                                                </div>
                                                <div className="text-xs text-slate-500">{u.email}</div>
                                                <div className="text-[10px] text-slate-600 font-mono">{u.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <RoleBadge role={u.role} />
                                    </td>
                                    <td className="p-4 text-xs text-slate-400 text-sm font-mono">
                                        {u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="p-4 text-right flex items-center justify-end gap-2">
                                        <select 
                                            value={u.role || 'user'} 
                                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                            className="bg-slate-900 border border-slate-600 text-xs rounded px-2 py-1 text-white outline-none focus:border-indigo-500"
                                        >
                                            <option value="user">User</option>
                                            <option value="vip">VIP</option>
                                            <option value="mod">Mod</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                        <button 
                                            onClick={() => handleBanToggle(u)}
                                            className={`p-1 rounded border transition-colors ${u.isBanned ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-red-500/20 border-red-500/50 text-red-400'}`}
                                            title={u.isBanned ? t.admin.unbanUser : t.admin.banUser}
                                        >
                                            {u.isBanned ? <CheckCircle size={14}/> : <Ban size={14}/>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {processedUsers.length === 0 && <div className="p-8 text-center text-slate-500">No users found.</div>}
                </div>
            )}

            {/* EDIT MODAL */}
            {editingOutfit && (
                <EditOutfitModal 
                    outfit={editingOutfit} 
                    onClose={() => setEditingOutfit(null)} 
                    onSave={handleEditSave} 
                    categories={categories}
                    t={t}
                />
            )}
        </div>
    );
};

const FAQ = () => {
    const { t } = useContext(LanguageContext);
    return (
        <div className="max-w-3xl mx-auto px-4 py-12 animate-fade-in">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">{t.faq.title}</h2>
            <div className="space-y-4">
                {t.faq.items.map((item, i) => (
                    <div key={i} className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-sm hover:border-indigo-500/50 transition-colors">
                        <h3 className="font-bold text-white mb-2 flex items-center gap-2"><Info size={16} className="text-indigo-500"/> {item.q}</h3>
                        <p className="text-slate-400 pl-6">{item.a}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ==========================================
// --- MAIN APP COMPONENT ---
// ==========================================

export default function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); 
  const [authLoading, setAuthLoading] = useState(true); 
  const [allOutfits, setAllOutfits] = useState([]);
  const [categories, setCategories] = useState(['Streetwear', 'Casual', 'Y2K', 'Cyberpunk', 'Goth', 'Soft']);
  const [moderators, setModerators] = useState([]); 
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  
  const [location, navigate] = useHashLocation();

  // ROUTING LOGIC
  let view = 'home';
  let viewParams = {};

  if (location === '/' || location === '') view = 'home';
  else if (location.startsWith('/gallery')) view = 'gallery';
  else if (location.startsWith('/submit')) view = 'submit';
  else if (location.startsWith('/faq')) view = 'faq';
  else if (location.startsWith('/tos')) view = 'tos';
  else if (location.startsWith('/contact')) view = 'contact';
  else if (location.startsWith('/login')) view = 'login';
  else if (location.startsWith('/admin')) view = 'admin';
  else if (location.startsWith('/outfit/')) {
      view = 'detail';
      viewParams.id = location.split('/outfit/')[1];
  }
  else if (location.startsWith('/profile')) {
      view = 'profile';
      const parts = location.split('/profile/');
      if (parts.length > 1) {
          // FIX: Clean ID from query params
          viewParams.userId = parts[1].split('?')[0];
      }
      else if (user) viewParams.userId = user.uid; 
      else view = 'login';
  }

  // Custom Global Styles
  const customStyles = `
    .custom-scrollbar::-webkit-scrollbar { height: 8px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #1e293b; border-radius: 4px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .animate-spin { animation: spin 1s linear infinite; }
  `;

  // Language Setup
  const getBrowserLang = () => navigator.language.startsWith('de') ? 'de' : 'en';
  const [lang, setLang] = useState(localStorage.getItem('language') || getBrowserLang());
  const changeLanguage = (newLang) => { setLang(newLang); localStorage.setItem('language', newLang); };
  const t = translations[lang];

  // Auth Listener
  useEffect(() => { 
      const unsubscribe = onAuthStateChanged(auth, async (currentUser) => { 
          setUser(currentUser);
          if (currentUser && !currentUser.isAnonymous) {
              // FIX: LIVE LISTENER FOR USER PROFILE UPDATES
              // Instead of just getDoc, we use onSnapshot on the profile doc
              // This ensures profile image updates reflect immediately in UI
              const profileRef = doc(db, 'artifacts', appId, 'public', 'data', 'profiles', currentUser.uid);
              
              // We setup a listener for THIS user's profile
              const unsubProfile = onSnapshot(profileRef, (docSnap) => {
                  if (docSnap.exists()) {
                      setUserProfile(docSnap.data());
                  } else {
                      // Create if missing
                      const newProfile = { role: 'user', username: currentUser.displayName, email: currentUser.email };
                      setDoc(profileRef, newProfile);
                      setUserProfile(newProfile);
                  }
              });
              
              // Store unsub function somewhere if needed, but for app-level auth it's okay to keep running
          } else {
              setUserProfile(null);
          }
          setAuthLoading(false);
      }); 
      return () => unsubscribe(); 
  }, []);
  
  // Data Fetching
  useEffect(() => {
     const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'outfits'), orderBy('createdAt', 'desc'));
     const unsubscribeData = onSnapshot(q, (snapshot) => { setAllOutfits(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); }, (error) => console.error("Data Error", error));
     return () => unsubscribeData();
  }, [user]); 

  // Settings Fetching
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config'), (docSnap) => { 
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.categories) setCategories(data.categories);
            if (data.maintenanceMode !== undefined) setMaintenanceMode(data.maintenanceMode);
            if (data.announcement) {
                setAnnouncement(data.announcement); 
                setShowAnnouncement(true); 
            } else setAnnouncement(null);
        }
    }); return () => unsub();
  }, []);

  const handleLogout = () => { signOut(auth); navigate('/'); };

  // CALCULATE ROLE BEFORE USING IT IN MEMOS
  const userRole = useMemo(() => {
      if (!user || user.isAnonymous) return 'guest';
      if (SUPER_ADMINS.includes(user.email)) return 'admin';
      return userProfile?.role || 'user';
  }, [user, userProfile]);

  const isMod = userRole === 'admin' || userRole === 'mod';
  const isAdmin = userRole === 'admin';
  const isBanned = userProfile?.isBanned;

  const approvedOutfits = useMemo(() => allOutfits.filter(o => o.status === 'approved' || !o.status || (isMod && o.status === 'inactive')), [allOutfits, isMod]);
  const pendingOutfits = useMemo(() => allOutfits.filter(o => o.status === 'pending'), [allOutfits]);
  const rejectedOutfits = useMemo(() => allOutfits.filter(o => o.status === 'rejected'), [allOutfits]);

  if (authLoading) return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-indigo-500 gap-4">
          <div className="w-12 h-12 border-4 border-current border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 text-sm animate-pulse">Lade BloxStyle...</p>
      </div>
  );

  // Banned Screen Intercept
  if (isBanned && user) {
      return (
          <LanguageContext.Provider value={{ t, lang, changeLanguage }}>
              <BannedScreen onLogout={handleLogout} />
          </LanguageContext.Provider>
      );
  }

  if (maintenanceMode && !isMod && view !== 'login') {
      return (
        <LanguageContext.Provider value={{ t, lang, changeLanguage }}>
            <style>{customStyles}</style>
            <MaintenanceScreen navigate={navigate} />
        </LanguageContext.Provider>
      );
  }

  return (
    <LanguageContext.Provider value={{ t, lang, changeLanguage }}>
        <UIProvider t={t}>
            <style>{customStyles}</style>
            <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col">
                <Navbar navigate={navigate} view={view} user={user} userProfile={userProfile} onLogout={handleLogout} goToProfile={(uid) => navigate('/profile/' + uid)} goToSettings={() => navigate(`/profile/${user.uid}?edit=true`)} />
                
                <main className="flex-grow">
                    {view === 'home' && <Home navigate={navigate} featuredOutfits={approvedOutfits} />}
                    {view === 'gallery' && <Gallery outfits={approvedOutfits} navigate={navigate} categories={categories} />}
                    {view === 'submit' && <SubmitOutfit categories={categories} user={user} navigate={navigate} />}
                    {view === 'detail' && <OutfitDetail outfitId={viewParams.id} onBack={() => navigate('/gallery')} allOutfits={allOutfits} navigate={navigate} user={user} userProfile={userProfile} currentUserRole={userRole} />}
                    {view === 'faq' && <FAQ />}
                    {view === 'tos' && <TOS />}
                    {view === 'contact' && <Contact />}
                    {view === 'login' && <Auth navigate={navigate} />}
                    {view === 'profile' && <UserProfile user={user} userProfile={userProfile} userRole={userRole} profileId={viewParams.userId} currentUser={user} navigate={navigate} outfits={allOutfits} isSettingsMode={window.location.hash.includes('edit=true')} />}
                    {view === 'admin' && <AdminDashboard 
                        outfits={approvedOutfits} 
                        pendingOutfits={pendingOutfits} 
                        rejectedOutfits={rejectedOutfits} 
                        user={user} 
                        userRole={userRole} 
                        categories={categories} 
                        setCategories={setCategories} 
                        maintenanceMode={maintenanceMode} 
                        setMaintenanceMode={setMaintenanceMode} 
                        moderators={moderators} 
                        setModerators={setModerators} 
                        announcement={announcement} 
                        setAnnouncement={setAnnouncement} 
                    />}
                </main>

                {showAnnouncement && <AnnouncementPopup message={announcement} onClose={() => setShowAnnouncement(false)} />}
                
                <footer className="bg-slate-900 border-t border-slate-800 py-12 mt-auto">
                    <div className="max-w-7xl mx-auto px-4 text-center">
                        <div className="flex justify-center items-center gap-2 mb-4">
                             <BloxLogo className="w-8 h-8 text-slate-600"/>
                             <span className="text-slate-500 font-bold tracking-widest">BLOXSTYLE</span>
                        </div>
                        <p className="text-slate-500 text-sm mb-6">{t.footer}</p>
                        
                        <div className="flex flex-col md:flex-row justify-center items-center gap-8 text-sm font-medium">
                            <div className="flex gap-6">
                                <button onClick={() => navigate('/faq')} className="text-slate-400 hover:text-white transition-colors">FAQ</button>
                                <button onClick={() => navigate('/tos')} className="text-slate-400 hover:text-white transition-colors flex items-center gap-1"><FileText size={14}/> {t.nav.tos}</button>
                                <button onClick={() => navigate('/contact')} className="text-slate-400 hover:text-white transition-colors">{t.nav.contact}</button>
                            </div>

                            <div className="h-px w-full md:w-px md:h-4 bg-slate-800"></div>

                            <div className="flex gap-6">
                                <a href={DISCORD_INVITE_LINK} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-2">
                                    <MessageCircle size={16}/> {t.discord}
                                </a>
                                <a href={ROBLOX_GROUP_LINK} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                                    <Gamepad2 size={16}/> {t.robloxGroup}
                                </a>
                                <a href={TIKTOK_LINK} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-400 transition-colors flex items-center gap-2">
                                    <Video size={16}/> {t.tiktok}
                                </a>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </UIProvider>
    </LanguageContext.Provider>
  );
}